
import React, { useState, useEffect, useRef } from 'react';
import { ArtItem, Order, OrderStatus, ChatThread, Comment, ChatMessage, ArtCategory, Language, CartItem, DailyVisit, CommissionTier, SiteSettings, User as UserType, ProfileScreenshot } from '../types';
import { Plus, Trash2, X, Flower, CheckCircle2, XCircle, Clock, Hash, User, Calendar, DollarSign, ExternalLink, Box, Loader2, Save, Image as ImageIcon, Upload, Ban, ChevronRight, Download, RefreshCw, AlertCircle, ToggleLeft, ToggleRight, Link as LinkIcon, MessageSquare, Move, Type, Palette, LayoutGrid, RotateCcw, FileVideo, Globe, Edit2, ChevronDown, ChevronUp, UserCheck, CloudUpload, BarChart3, TrendingUp, Users, Pipette, ShieldCheck, Star, Sparkles, Zap, Settings, HelpCircle, CreditCard, Heart, Search, ArrowUp, ArrowDown, ShoppingBag, PackageCheck, Receipt, FileSpreadsheet, Layers, Award, Eye, Filter, ArrowUpDown, MapPin, Compass, Navigation, Copy } from 'lucide-react';
import { database } from '../services/databaseService';
import { storage } from '../services/storageService';
import { MASTER_FALLBACK_IMAGE, DEFAULT_CATEGORY_COVERS, ART_SUBCATEGORIES, ART_TYPES } from '../constants';
import { formatBrazilDateTime } from '../utils';
import { aggregateGeoStats, getCountryFlag, AggregatedCountryItem, AggregatedRegionItem } from '../geoUtils';

interface AdminPanelProps {
  arts: ArtItem[];
  orders: Order[];
  chatThreads: ChatThread[];
  comments: Comment[];
  categoryCovers: Record<string, string>;
  commissionTiers: CommissionTier[];
  siteSettings: SiteSettings;
  profileScreenshots?: ProfileScreenshot[];
  onUpdateArts: (updater: ArtItem[] | ((prev: ArtItem[]) => ArtItem[])) => void;
  onUpdateOrders: (updater: Order[] | ((prev: Order[]) => Order[])) => void;
  onUpdateSupport: (updater: any) => void;
  onUpdateChatThreads: (updater: ChatThread[] | ((prev: ChatThread[]) => ChatThread[])) => void;
  onUpdateComments: (updater: Comment[] | ((prev: Comment[]) => Comment[])) => void;
  onUpdateCategoryCovers: (covers: Record<string, string>) => void;
  onUpdateCommissionTiers: (tiers: CommissionTier[]) => void;
  onUpdateSiteSettings: (settings: SiteSettings) => void;
  onUpdateProfileScreenshots?: (shots: ProfileScreenshot[]) => void;
  onClose: () => void;
  language: Language;
  notifications?: { orders: number, chats: number };
  onClearNotifications?: (type: 'orders' | 'chats') => void;
}

const getAspectClass = (item: ArtItem) => {
  if (item.aspectRatio === 'horizontal') return 'aspect-video';
  if (item.aspectRatio === 'square') return 'aspect-square';
  if (item.aspectRatio === 'vertical') return 'aspect-[9/14]';

  // Fallback to auto-detection for older items
  const categoryLower = (item.category || '').toLowerCase();
  const subcategoryLower = (item.subcategory || '').toLowerCase();
  const artTypeLower = (item.artType || '').toLowerCase();
  const titlePtLower = (item.title?.pt || '').toLowerCase();
  const titleEnLower = (item.title?.en || '').toLowerCase();

  if (
    categoryLower.includes('avatar') || 
    artTypeLower.includes('avatar') || 
    subcategoryLower.includes('avatar') ||
    titlePtLower.includes('avatar') ||
    titleEnLower.includes('avatar')
  ) {
    return 'aspect-square';
  }

  if (
    categoryLower.includes('banner') ||
    categoryLower.includes('wallpaper') ||
    categoryLower.includes('background') ||
    subcategoryLower.includes('banner') ||
    subcategoryLower.includes('wallpaper') ||
    subcategoryLower.includes('background') ||
    artTypeLower.includes('banner') ||
    titlePtLower.includes('banner') ||
    titleEnLower.includes('banner') ||
    titlePtLower.includes('header') ||
    titleEnLower.includes('header') ||
    titlePtLower.includes('fundo') ||
    titleEnLower.includes('background')
  ) {
    return 'aspect-video';
  }

  return 'aspect-[9/14]';
};

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  arts, orders, chatThreads, comments, categoryCovers, commissionTiers: initialCommissionTiers, siteSettings,
  profileScreenshots = [], onUpdateArts, onUpdateOrders, onUpdateChatThreads, onUpdateComments, onUpdateCategoryCovers, onUpdateCommissionTiers, onUpdateSiteSettings, onUpdateProfileScreenshots, onClose,
  language, notifications, onClearNotifications
}) => {
  const [activeTab, setActiveTab] = useState<'gallery' | 'orders' | 'users' | 'chats' | 'categories' | 'commissions' | 'reviews' | 'screenshots' | 'stats' | 'settings' | 'footer' | 'faq'>('gallery');
  const [selectedThreadEmail, setSelectedThreadEmail] = useState<string | null>(null);
  const [adminReply, setAdminReply] = useState('');
  const [commentReplyTexts, setCommentReplyTexts] = useState<Record<string, string>>({});
  const [isSyncing, setIsSyncing] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // States for User Profile Screenshots tab
  const [screenshotsList, setScreenshotsList] = useState<ProfileScreenshot[]>(profileScreenshots);
  const [isLoadingScreenshots, setIsLoadingScreenshots] = useState(false);

  useEffect(() => {
    if (profileScreenshots && profileScreenshots.length > 0) {
      setScreenshotsList(profileScreenshots);
    }
  }, [profileScreenshots]);
  const [newShotTitle, setNewShotTitle] = useState('');
  const [newShotUserName, setNewShotUserName] = useState('');
  const [newShotSteamUrl, setNewShotSteamUrl] = useState('');
  const [shotPendingFile, setShotPendingFile] = useState<File | null>(null);
  const [shotPreviewUrl, setShotPreviewUrl] = useState<string | null>(null);
  const shotFileInputRef = useRef<HTMLInputElement>(null);

  // States for Users & Sales tab
  const [usersList, setUsersList] = useState<UserType[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState<'all' | 'buyers' | 'non-buyers'>('all');
  const [userSortBy, setUserSortBy] = useState<'spent_desc' | 'orders_desc' | 'name_asc' | 'recent'>('spent_desc');
  const [usersSubTab, setUsersSubTab] = useState<'users' | 'sold_items' | 'ranking'>('users');
  const [soldItemsSearch, setSoldItemsSearch] = useState('');
  const [soldItemsCategory, setSoldItemsCategory] = useState<string>('all');
  const [soldItemsStatus, setSoldItemsStatus] = useState<string>('all');
  const [soldItemsSort, setSoldItemsSort] = useState<'recent' | 'price_desc' | 'price_asc'>('recent');
  const [expandedUserOrderId, setExpandedUserOrderId] = useState<string | null>(null);
  
  const [commissionTiers, setCommissionTiers] = useState<CommissionTier[]>(initialCommissionTiers);
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [tierPreview, setTierPreview] = useState<string | null>(null);
  const [tierPendingFile, setTierPendingFile] = useState<File | null>(null);
  const tierFileInputRef = useRef<HTMLInputElement>(null);

  const [analytics, setAnalytics] = useState<DailyVisit[]>([]);
  const [geoPeriod, setGeoPeriod] = useState<'today' | '7days' | '30days' | 'all'>('30days');
  const [geoSearchQuery, setGeoSearchQuery] = useState('');
  const [geoSortBy, setGeoSortBy] = useState<'visitors_desc' | 'hits_desc' | 'name_asc'>('visitors_desc');
  const [expandedCountryCode, setExpandedCountryCode] = useState<string | null>(null);
  const [isAddingArt, setIsAddingArt] = useState(false);
  const [siteSettingsState, setSiteSettingsState] = useState(siteSettings);
  const [customIntroLang, setCustomIntroLang] = useState<'pt' | 'en' | 'es' | 'ru'>('pt');

  useEffect(() => {
    if (siteSettings) {
      setSiteSettingsState(siteSettings);
    }
  }, [siteSettings]);

  const [editingArt, setEditingArt] = useState<ArtItem | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [coverPendingFile, setCoverPendingFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const [isDragOverCover, setIsDragOverCover] = useState(false);
  const [isAutoExtractingCover, setIsAutoExtractingCover] = useState(false);
  const [isOptimizingCovers, setIsOptimizingCovers] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState<{ current: number; total: number } | null>(null);
  const coverFileInputRef = useRef<HTMLInputElement>(null);

  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [catCoverPreview, setCatCoverPreview] = useState<string | null>(null);
  const [catPendingFile, setCatPendingFile] = useState<File | null>(null);
  const catFileInputRef = useRef<HTMLInputElement>(null);

  const [newArt, setNewArt] = useState<Partial<ArtItem>>({
    title: { pt: '', en: '' },
    category: ArtCategory.ANIMATED,
    artType: 'Single Artwork',
    price: 3.00,
    description: { pt: '', en: '' },
    isAnimated: true,
    steamBackgroundUrl: '',
    notForSale: false,
    hideSignature: false,
    hideDiscord: false,
    hideNotes: false,
    hideBuyButton: false,
    downloadUrl: ''
  });

  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFormRef = useRef<HTMLDivElement>(null);
  
  const safeOrders = Array.isArray(orders) ? orders : [];
  const safeArts = Array.isArray(arts) ? arts : [];

  const [gallerySearch, setGallerySearch] = useState('');
  const [gallerySortBy, setGallerySortBy] = useState<'default' | 'date' | 'price'>('default');
  const [gallerySortOrder, setGallerySortOrder] = useState<'asc' | 'desc'>('desc');

  const filteredAdminArts = React.useMemo(() => {
    let result = [...safeArts];

    if (gallerySearch.trim()) {
      const q = gallerySearch.toLowerCase();
      result = result.filter(art => 
        (art.title?.pt && art.title.pt.toLowerCase().includes(q)) ||
        (art.title?.en && art.title.en.toLowerCase().includes(q)) ||
        (art.category && art.category.toLowerCase().includes(q)) ||
        (art.subcategory && art.subcategory.toLowerCase().includes(q)) ||
        (art.id && art.id.toLowerCase().includes(q))
      );
    }

    if (gallerySortBy === 'price') {
      const forSaleList = result.filter(art => !art.notForSale && (art.price || 0) > 0);
      return forSaleList.sort((a, b) => {
        const priceA = a.price || 0;
        const priceB = b.price || 0;
        return gallerySortOrder === 'asc' ? priceA - priceB : priceB - priceA;
      });
    } else if (gallerySortBy === 'date') {
      result.sort((a, b) => {
        const getTimestamp = (item: ArtItem) => {
          if (!item.id) return 0;
          const cleanId = item.id.replace('ART-', '').replace('DEMO-', '');
          const num = parseInt(cleanId, 10);
          if (!isNaN(num)) {
            return item.id.startsWith('DEMO-') ? num * 1000 : num;
          }
          return 0;
        };
        const timeA = getTimestamp(a);
        const timeB = getTimestamp(b);
        return gallerySortOrder === 'asc' ? timeA - timeB : timeB - timeA;
      });
    }

    return result;
  }, [safeArts, gallerySearch, gallerySortBy, gallerySortOrder]);

  const cleanComments = React.useMemo(() => {
    return Array.isArray(comments)
      ? comments
          .filter(c => c && c.id && !c.id.startsWith('SEED-'))
          .filter((comment, index, self) => self.findIndex(c => c.id === comment.id) === index)
      : [];
  }, [comments]);

  const todayDateStr = React.useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date());
    } catch (e) {
      return new Date().toISOString().split('T')[0];
    }
  }, []);

  const todayStats = React.useMemo(() => {
    if (!analytics || analytics.length === 0) {
      return { uniqueCount: 0, totalCount: 0 };
    }
    const match = analytics.find(item => item.date === todayDateStr);
    if (match) {
      return {
        uniqueCount: match.uniqueCount || match.count || 0,
        totalCount: match.totalCount || match.count || 0
      };
    }
    const last = analytics[analytics.length - 1];
    return {
      uniqueCount: last?.uniqueCount || last?.count || 0,
      totalCount: last?.totalCount || last?.count || 0
    };
  }, [analytics, todayDateStr]);

  const geoStats = React.useMemo(() => {
    return aggregateGeoStats(analytics, geoPeriod);
  }, [analytics, geoPeriod]);

  const filteredGeoCountries = React.useMemo(() => {
    let list = [...geoStats.countries];
    if (geoSearchQuery.trim()) {
      const q = geoSearchQuery.toLowerCase();
      list = list.filter(c => 
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.regions.some(r => r.name.toLowerCase().includes(q))
      );
    }
    if (geoSortBy === 'visitors_desc') {
      list.sort((a, b) => b.uniqueCount - a.uniqueCount || b.totalCount - a.totalCount);
    } else if (geoSortBy === 'hits_desc') {
      list.sort((a, b) => b.totalCount - a.totalCount || b.uniqueCount - a.uniqueCount);
    } else if (geoSortBy === 'name_asc') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [geoStats, geoSearchQuery, geoSortBy]);

  useEffect(() => {
    refreshAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 'orders') {
      refreshOrders();
      onClearNotifications?.('orders');
    }
    if (activeTab === 'users') {
      refreshUsers();
      refreshOrders();
    }
    if (activeTab === 'chats') {
      refreshChats();
      onClearNotifications?.('chats');
    }
    if (activeTab === 'stats') refreshAnalytics();
    if (activeTab === 'commissions') refreshCommissionTiers();
    if (activeTab === 'screenshots') refreshScreenshots();
    if (activeTab === 'settings') {
      setSiteSettingsState(siteSettings);
    }
  }, [activeTab, siteSettings]);

  const refreshScreenshots = async () => {
    setIsLoadingScreenshots(true);
    const data = await database.getProfileScreenshots();
    setScreenshotsList(data);
    onUpdateProfileScreenshots?.(data);
    setIsLoadingScreenshots(false);
  };

  const handleUploadScreenshot = async () => {
    if (!shotPendingFile) {
      alert(language === 'pt' ? 'Selecione uma imagem de print do perfil primeiro.' : 'Please select a profile screenshot image first.');
      return;
    }
    setIsSyncing(true);
    try {
      const res = await database.uploadProfileScreenshot(shotPendingFile, {
        title: newShotTitle,
        userName: newShotUserName,
        steamUrl: newShotSteamUrl
      });
      if (res && res.success && res.item) {
        const updated = [res.item!, ...screenshotsList.filter(s => s.id !== res.item!.id)];
        setScreenshotsList(updated);
        onUpdateProfileScreenshots?.(updated);
        setShotPendingFile(null);
        setShotPreviewUrl(null);
        setNewShotTitle('');
        setNewShotUserName('');
        setNewShotSteamUrl('');
        if (shotFileInputRef.current) shotFileInputRef.current.value = '';
        alert(language === 'pt' ? 'Screenshot adicionada com sucesso!' : 'Screenshot added successfully!');
      } else {
        // Fallback local instantâneo
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => resolve(URL.createObjectURL(shotPendingFile));
          reader.readAsDataURL(shotPendingFile);
        });
        const fallbackItem: ProfileScreenshot = {
          id: 'SHOT-' + Date.now(),
          imageUrl: base64,
          title: newShotTitle || '',
          userName: newShotUserName || '',
          steamUrl: newShotSteamUrl || '',
          date: new Date().toISOString()
        };
        const updated = [fallbackItem, ...screenshotsList.filter(s => s.id !== fallbackItem.id)];
        setScreenshotsList(updated);
        onUpdateProfileScreenshots?.(updated);
        await database.saveProfileScreenshots(updated);
        setShotPendingFile(null);
        setShotPreviewUrl(null);
        setNewShotTitle('');
        setNewShotUserName('');
        setNewShotSteamUrl('');
        if (shotFileInputRef.current) shotFileInputRef.current.value = '';
        alert(language === 'pt' ? 'Screenshot salva com sucesso!' : 'Screenshot saved successfully!');
      }
    } catch (err) {
      console.error("Erro upload screenshot:", err);
      // Fallback em caso de exceção
      try {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => resolve(URL.createObjectURL(shotPendingFile));
          reader.readAsDataURL(shotPendingFile);
        });
        const fallbackItem: ProfileScreenshot = {
          id: 'SHOT-' + Date.now(),
          imageUrl: base64,
          title: newShotTitle || '',
          userName: newShotUserName || '',
          steamUrl: newShotSteamUrl || '',
          date: new Date().toISOString()
        };
        const updated = [fallbackItem, ...screenshotsList.filter(s => s.id !== fallbackItem.id)];
        setScreenshotsList(updated);
        onUpdateProfileScreenshots?.(updated);
        await database.saveProfileScreenshots(updated);
        setShotPendingFile(null);
        setShotPreviewUrl(null);
        setNewShotTitle('');
        setNewShotUserName('');
        setNewShotSteamUrl('');
        if (shotFileInputRef.current) shotFileInputRef.current.value = '';
        alert(language === 'pt' ? 'Screenshot salva com sucesso!' : 'Screenshot saved successfully!');
      } catch (e) {
        alert(language === 'pt' ? 'Screenshot enviada com sucesso!' : 'Screenshot uploaded successfully!');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteScreenshot = async (id: string) => {
    if (!confirm(language === 'pt' ? 'Tem certeza que deseja excluir esta screenshot?' : 'Are you sure you want to delete this screenshot?')) {
      return;
    }
    setIsSyncing(true);
    const ok = await database.deleteProfileScreenshot(id);
    const filtered = screenshotsList.filter(s => s.id !== id);
    setScreenshotsList(filtered);
    onUpdateProfileScreenshots?.(filtered);
    if (!ok) {
      await database.saveProfileScreenshots(filtered);
    }
    setIsSyncing(false);
  };

  const refreshCommissionTiers = async () => {
    setIsSyncing(true);
    const data = await database.getCommissionTiers();
    setCommissionTiers(data);
    setIsSyncing(false);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (confirm(language === 'pt' ? 'Tem certeza que deseja deletar esta avaliação?' : 'Are you sure you want to delete this review?')) {
      const updated = comments.filter(c => c.id !== commentId);
      onUpdateComments(updated);
      await database.saveAllComments(updated);
    }
  };

  const handleSaveReply = async (commentId: string, text: string) => {
    if (!text.trim()) return;
    const updated = comments.map(c => {
      if (c.id === commentId) {
        return { ...c, reply: text };
      }
      return c;
    });
    onUpdateComments(updated);
    await database.saveAllComments(updated);
    setCommentReplyTexts(prev => ({ ...prev, [commentId]: '' }));
  };

  const handleDeleteReply = async (commentId: string) => {
    if (confirm(language === 'pt' ? 'Tem certeza que deseja remover esta resposta?' : 'Are you sure you want to delete this reply?')) {
      const updated = comments.map(c => {
        if (c.id === commentId) {
          const { reply, ...rest } = c;
          return rest;
        }
        return c;
      });
      onUpdateComments(updated);
      await database.saveAllComments(updated);
    }
  };

  useEffect(() => {
    setSiteSettingsState(siteSettings);
  }, [siteSettings]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedThreadEmail, chatThreads, activeTab]);

  const refreshAnalytics = async () => {
    setIsSyncing(true);
    const data = await database.getAnalytics();
    if (data && data.length > 0) {
      setAnalytics(data);
    }
    setIsSyncing(false);
  };

  const handleTestVisit = async () => {
    setIsSyncing(true);
    const res = await database.logVisit();
    if (res.analytics && res.analytics.length > 0) {
      setAnalytics(res.analytics);
    } else {
      const data = await database.getAnalytics();
      if (data && data.length > 0) {
        setAnalytics(data);
      }
    }
    setIsSyncing(false);
    if (res.success) {
      alert("✅ Visita e localização geográfica registradas com sucesso no servidor!");
    } else {
      alert("⚠️ Visita enviada. Verifique a conexão com o servidor ou permissão de gravação.");
    }
  };

  const isVideoUrl = (url?: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('blob:') || lower.startsWith('data:video/');
  };

  const refreshOrders = async () => {
    setIsSyncing(true);
    const res = await database.getOrders();
    if (res.data) onUpdateOrders(res.data);
    setIsSyncing(false);
  };

  const refreshChats = async () => {
    setIsSyncing(true);
    const chats = await database.getChatThreads();
    if (chats) onUpdateChatThreads(chats);
    setIsSyncing(false);
  };

  const handleGlobalSyncCovers = async () => {
    if (!confirm("Confirmar sincronização com servidor Hostinger?")) return;
    setIsSyncing(true);
    const success = await database.syncAllCoversToServer(categoryCovers);
    if (success) alert("Santuário Global Sincronizado!");
    setIsSyncing(false);
  };

  const handleDownloadCoversJSON = () => {
    const cleanCovers: Record<string, string> = {};
    Object.entries(categoryCovers).forEach(([cat, url]: [string, string]) => {
      if (url.includes('/uploads/')) {
        cleanCovers[cat] = url.split('/').pop() || '';
      } else {
        cleanCovers[cat] = url;
      }
    });
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(cleanCovers, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "category_covers.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const resolveCustomerDisplayName = (userEmail: string, givenName?: string): string => {
    if (!userEmail) return givenName || 'Cliente';
    const emailLower = userEmail.toLowerCase();
    
    // Check givenName first if it's a real non-generic name
    if (givenName && givenName.trim()) {
      const gLower = givenName.trim().toLowerCase();
      if (!['usuário', 'usuario', 'user', 'cliente'].includes(gLower) && !gLower.includes('@')) {
        return givenName.trim();
      }
    }

    // Check in orders for a matching userEmail
    const matchingOrder = safeOrders.find(o => o.userEmail && o.userEmail.toLowerCase() === emailLower && o.userName);
    if (matchingOrder && matchingOrder.userName) {
      const oLower = matchingOrder.userName.trim().toLowerCase();
      if (!['usuário', 'usuario', 'user', 'cliente'].includes(oLower) && !oLower.includes('@')) {
        return matchingOrder.userName.trim();
      }
    }

    // Check in users list for a matching email
    const matchingUser = usersList.find(u => u.email && u.email.toLowerCase() === emailLower && u.name);
    if (matchingUser && matchingUser.name) {
      const uLower = matchingUser.name.trim().toLowerCase();
      if (!['usuário', 'usuario', 'user', 'cliente'].includes(uLower) && !uLower.includes('@')) {
        return matchingUser.name.trim();
      }
    }

    // Fallback: derive a formatted name from email address prefix (e.g. "alex.silva" -> "Alex Silva")
    const prefix = userEmail.split('@')[0] || '';
    if (prefix) {
      return prefix
        .replace(/[._-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }

    return 'Cliente';
  };

  const handleSendAdminReply = async () => {
    if (!adminReply.trim() || !selectedThreadEmail) return;
    const thread = chatThreads.find(t => t.userEmail === selectedThreadEmail);
    const resolvedName = resolveCustomerDisplayName(selectedThreadEmail, thread?.userName);
    const newMessage: ChatMessage = {
      id: `ADMIN-${Date.now()}`,
      sender: 'admin',
      text: adminReply,
      timestamp: new Date().toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })
    };
    const success = await database.saveChatMessage(selectedThreadEmail, resolvedName, newMessage);
    if (!success) {
      alert("Erro ao salvar mensagem no servidor Hostinger. Verifique permissões do arquivo chats.json.");
    }
    onUpdateChatThreads(prev => {
      const up = [...prev];
      const idx = up.findIndex(t => t.userEmail === selectedThreadEmail);
      if (idx > -1) {
        up[idx] = { 
          ...up[idx], 
          userName: resolvedName,
          messages: [...up[idx].messages, newMessage], 
          lastUpdated: new Date().toISOString() 
        };
      }
      return up;
    });
    setAdminReply('');
  };

  const refreshUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const list = await database.getAllUsers();
      setUsersList(list || []);
    } catch (e) {
      console.error("Erro ao carregar lista de usuários:", e);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (email: string) => {
    const confirmMsg = language === 'en' 
      ? `Are you sure you want to delete user ${email}?`
      : language === 'es'
      ? `¿Estás seguro de que deseas eliminar al usuario ${email}?`
      : language === 'ru'
      ? `Вы уверены, что хотите удалить пользователя ${email}?`
      : `Tem certeza que deseja excluir o usuário ${email}?`;
    if (!confirm(confirmMsg)) return;

    setIsLoadingUsers(true);
    const success = await database.deleteUser(email);
    if (success) {
      setUsersList(prev => prev.filter(u => u.email.toLowerCase() !== email.toLowerCase()));
      if (selectedUserEmail?.toLowerCase() === email.toLowerCase()) {
        setSelectedUserEmail(null);
      }
    }
    setIsLoadingUsers(false);
  };

  // Structured list of all sold items across all orders
  const allSoldItems = React.useMemo(() => {
    const items: Array<{
      uniqueKey: string;
      orderId: string;
      orderDate: string;
      orderStatus: OrderStatus;
      userEmail: string;
      userName: string;
      item: CartItem;
      itemTitle: string;
      itemCategory: string;
      itemSubcategory?: string;
      itemPrice: number;
      quantity: number;
      totalAmount: number;
      imageUrl: string;
      videoUrl?: string;
      downloadUrl?: string;
      customSignature?: string;
      sigColor?: string;
      customFont?: string;
      dafontLink?: string;
      discord?: string;
      steamUrl?: string;
      notes?: string;
    }> = [];

    safeOrders.forEach(order => {
      (order.items || []).forEach((cartItem, idx) => {
        const titlePt = cartItem.title?.pt || cartItem.title?.en || 'Arte sem título';
        const titleEn = cartItem.title?.en || cartItem.title?.pt || 'Untitled Art';
        const itemTitle = language === 'en' ? titleEn : titlePt;
        const qty = cartItem.quantity || 1;
        const price = cartItem.price || 0;

        items.push({
          uniqueKey: `${order.id}_${cartItem.id || idx}_${idx}`,
          orderId: order.id,
          orderDate: formatBrazilDateTime(order.date),
          orderStatus: order.status,
          userEmail: order.userEmail,
          userName: order.userName || order.userEmail.split('@')[0],
          item: cartItem,
          itemTitle,
          itemCategory: cartItem.category || 'Geral',
          itemSubcategory: cartItem.subcategory,
          itemPrice: price,
          quantity: qty,
          totalAmount: price * qty,
          imageUrl: cartItem.imageUrl,
          videoUrl: cartItem.videoUrl,
          downloadUrl: cartItem.downloadUrl,
          customSignature: cartItem.customSignature,
          sigColor: cartItem.sigColor,
          customFont: cartItem.customFont,
          dafontLink: cartItem.dafontLink,
          discord: cartItem.discord,
          steamUrl: cartItem.steamUrl,
          notes: cartItem.notes
        });
      });
    });

    return items;
  }, [safeOrders, language]);

  // Overall platform sales metrics
  const salesMetrics = React.useMemo(() => {
    const totalRevenue = safeOrders
      .filter(o => o.status !== OrderStatus.CANCELLED)
      .reduce((sum, o) => sum + (o.total || 0), 0);

    const completedRevenue = safeOrders
      .filter(o => o.status === OrderStatus.COMPLETED)
      .reduce((sum, o) => sum + (o.total || 0), 0);

    const totalItemsCount = allSoldItems
      .filter(i => i.orderStatus !== OrderStatus.CANCELLED)
      .reduce((sum, i) => sum + i.quantity, 0);

    const totalOrdersCount = safeOrders.length;
    const completedOrdersCount = safeOrders.filter(o => o.status === OrderStatus.COMPLETED).length;
    const pendingOrdersCount = safeOrders.filter(o => o.status === OrderStatus.PENDING).length;

    const uniqueBuyersCount = new Set(safeOrders.map(o => o.userEmail.toLowerCase())).size;
    const totalUsersCount = Math.max(usersList.length, uniqueBuyersCount);

    const avgTicket = totalOrdersCount > 0 ? (totalRevenue / totalOrdersCount) : 0;

    return {
      totalRevenue,
      completedRevenue,
      totalItemsCount,
      totalOrdersCount,
      completedOrdersCount,
      pendingOrdersCount,
      uniqueBuyersCount,
      totalUsersCount,
      avgTicket
    };
  }, [safeOrders, allSoldItems, usersList]);

  // Ranking of top best-selling items
  const topSellingRanking = React.useMemo(() => {
    const map = new Map<string, {
      artId: string;
      title: string;
      category: string;
      imageUrl: string;
      videoUrl?: string;
      unitsSold: number;
      revenue: number;
      ordersCount: number;
      lastSoldDate: string;
    }>();

    allSoldItems.forEach(sold => {
      if (sold.orderStatus === OrderStatus.CANCELLED) return;
      const key = sold.item.id || sold.itemTitle;
      const existing = map.get(key);
      if (existing) {
        existing.unitsSold += sold.quantity;
        existing.revenue += sold.totalAmount;
        existing.ordersCount += 1;
        if (new Date(sold.orderDate) > new Date(existing.lastSoldDate || 0)) {
          existing.lastSoldDate = sold.orderDate;
        }
      } else {
        map.set(key, {
          artId: sold.item.id || 'N/A',
          title: sold.itemTitle,
          category: sold.itemCategory,
          imageUrl: sold.imageUrl,
          videoUrl: sold.videoUrl,
          unitsSold: sold.quantity,
          revenue: sold.totalAmount,
          ordersCount: 1,
          lastSoldDate: sold.orderDate
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => b.unitsSold - a.unitsSold || b.revenue - a.revenue);
  }, [allSoldItems]);

  // Aggregated stats per user
  const userPurchaseStats = React.useMemo(() => {
    const statsMap = new Map<string, {
      user: UserType;
      orders: Order[];
      totalSpent: number;
      itemsCount: number;
      firstOrderDate: string | null;
      lastOrderDate: string | null;
      completedOrdersCount: number;
      pendingOrdersCount: number;
    }>();

    usersList.forEach(u => {
      if (!u.email) return;
      const emailKey = u.email.toLowerCase();
      statsMap.set(emailKey, {
        user: u,
        orders: [],
        totalSpent: 0,
        itemsCount: 0,
        firstOrderDate: null,
        lastOrderDate: null,
        completedOrdersCount: 0,
        pendingOrdersCount: 0
      });
    });

    safeOrders.forEach(order => {
      if (!order.userEmail) return;
      const emailKey = order.userEmail.toLowerCase();
      let entry = statsMap.get(emailKey);
      if (!entry) {
        const fallbackUser: UserType = {
          name: order.userName || emailKey.split('@')[0],
          email: emailKey,
          avatarUrl: ''
        };
        entry = {
          user: fallbackUser,
          orders: [],
          totalSpent: 0,
          itemsCount: 0,
          firstOrderDate: null,
          lastOrderDate: null,
          completedOrdersCount: 0,
          pendingOrdersCount: 0
        };
        statsMap.set(emailKey, entry);
      }

      entry.orders.push(order);
      if (order.status !== OrderStatus.CANCELLED) {
        entry.totalSpent += (order.total || 0);
        const itemsInOrder = (order.items || []).reduce((acc, it) => acc + (it.quantity || 1), 0);
        entry.itemsCount += itemsInOrder;
      }
      if (order.status === OrderStatus.COMPLETED) entry.completedOrdersCount += 1;
      if (order.status === OrderStatus.PENDING) entry.pendingOrdersCount += 1;

      if (!entry.firstOrderDate || new Date(order.date) < new Date(entry.firstOrderDate)) {
        entry.firstOrderDate = order.date;
      }
      if (!entry.lastOrderDate || new Date(order.date) > new Date(entry.lastOrderDate)) {
        entry.lastOrderDate = order.date;
      }
    });

    return Array.from(statsMap.values());
  }, [usersList, safeOrders]);

  // Filtered & sorted users list
  const filteredUsers = React.useMemo(() => {
    let list = [...userPurchaseStats];

    if (userSearchQuery.trim()) {
      const q = userSearchQuery.toLowerCase();
      list = list.filter(item => 
        item.user.name.toLowerCase().includes(q) ||
        item.user.email.toLowerCase().includes(q)
      );
    }

    if (userFilter === 'buyers') {
      list = list.filter(item => item.orders.length > 0);
    } else if (userFilter === 'non-buyers') {
      list = list.filter(item => item.orders.length === 0);
    }

    if (userSortBy === 'spent_desc') {
      list.sort((a, b) => b.totalSpent - a.totalSpent);
    } else if (userSortBy === 'orders_desc') {
      list.sort((a, b) => b.orders.length - a.orders.length);
    } else if (userSortBy === 'name_asc') {
      list.sort((a, b) => a.user.name.localeCompare(b.user.name));
    } else if (userSortBy === 'recent') {
      list.sort((a, b) => {
        const timeA = a.lastOrderDate ? new Date(a.lastOrderDate).getTime() : 0;
        const timeB = b.lastOrderDate ? new Date(b.lastOrderDate).getTime() : 0;
        return timeB - timeA;
      });
    }

    return list;
  }, [userPurchaseStats, userSearchQuery, userFilter, userSortBy]);

  // Filtered & sorted sold items list
  const filteredSoldItems = React.useMemo(() => {
    let list = [...allSoldItems];

    if (soldItemsSearch.trim()) {
      const q = soldItemsSearch.toLowerCase();
      list = list.filter(i => 
        i.itemTitle.toLowerCase().includes(q) ||
        i.orderId.toLowerCase().includes(q) ||
        i.userName.toLowerCase().includes(q) ||
        i.userEmail.toLowerCase().includes(q) ||
        (i.customSignature && i.customSignature.toLowerCase().includes(q)) ||
        (i.discord && i.discord.toLowerCase().includes(q))
      );
    }

    if (soldItemsCategory !== 'all') {
      list = list.filter(i => i.itemCategory === soldItemsCategory);
    }

    if (soldItemsStatus !== 'all') {
      list = list.filter(i => i.orderStatus === soldItemsStatus);
    }

    if (soldItemsSort === 'recent') {
      list.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
    } else if (soldItemsSort === 'price_desc') {
      list.sort((a, b) => b.totalAmount - a.totalAmount);
    } else if (soldItemsSort === 'price_asc') {
      list.sort((a, b) => a.totalAmount - b.totalAmount);
    }

    return list;
  }, [allSoldItems, soldItemsSearch, soldItemsCategory, soldItemsStatus, soldItemsSort]);

  // Export handlers
  const handleExportSoldItemsCSV = () => {
    if (allSoldItems.length === 0) {
      alert(language === 'en' ? 'No sold items to export.' : 'Nenhum item vendido para exportar.');
      return;
    }
    const headers = ['Order ID', 'Order Date', 'Status', 'Buyer Name', 'Buyer Email', 'Item Title', 'Category', 'Unit Price ($)', 'Quantity', 'Total Amount ($)', 'Custom Signature', 'Discord', 'Steam URL'];
    const rows = allSoldItems.map(item => [
      `"${item.orderId}"`,
      `"${item.orderDate}"`,
      `"${item.orderStatus}"`,
      `"${(item.userName || '').replace(/"/g, '""')}"`,
      `"${item.userEmail}"`,
      `"${(item.itemTitle || '').replace(/"/g, '""')}"`,
      `"${item.itemCategory}"`,
      item.itemPrice.toFixed(2),
      item.quantity,
      item.totalAmount.toFixed(2),
      `"${(item.customSignature || '').replace(/"/g, '""')}"`,
      `"${(item.discord || '').replace(/"/g, '""')}"`,
      `"${(item.steamUrl || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `niiko_sold_items_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportSoldItemsJSON = () => {
    if (allSoldItems.length === 0) {
      alert(language === 'en' ? 'No sold items to export.' : 'Nenhum item vendido para exportar.');
      return;
    }
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(allSoldItems, null, 2));
    const link = document.createElement('a');
    link.setAttribute('href', dataStr);
    link.setAttribute('download', `niiko_sales_report_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportGeoCSV = () => {
    if (!geoStats.countries || geoStats.countries.length === 0) {
      alert(language === 'en' ? 'No geographic data to export.' : 'Nenhum dado geográfico para exportar.');
      return;
    }
    const headers = ['Country Name', 'ISO Code', 'Region / State', 'Unique Visitors', 'Total Hits', 'Traffic %'];
    const rows: string[][] = [];

    geoStats.countries.forEach(c => {
      if (c.regions && c.regions.length > 0) {
        c.regions.forEach(r => {
          rows.push([
            `"${c.name}"`,
            `"${c.code}"`,
            `"${r.name}"`,
            r.uniqueCount.toString(),
            r.totalCount.toString(),
            `${r.uniquePercentage.toFixed(1)}%`
          ]);
        });
      } else {
        rows.push([
          `"${c.name}"`,
          `"${c.code}"`,
          `"Principal"`,
          c.uniqueCount.toString(),
          c.totalCount.toString(),
          `${c.uniquePercentage.toFixed(1)}%`
        ]);
      }
    });

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `niiko_origem_geografica_${geoPeriod}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportGeoJSON = () => {
    if (!geoStats.countries || geoStats.countries.length === 0) {
      alert(language === 'en' ? 'No geographic data to export.' : 'Nenhum dado geográfico para exportar.');
      return;
    }
    const exportData = {
      period: geoPeriod,
      exportedAt: new Date().toISOString(),
      summary: {
        totalUniqueVisitors: geoStats.totalUniqueVisitors,
        totalPageHits: geoStats.totalPageHits,
        totalCountriesCount: geoStats.countries.length,
        topCountry: geoStats.topCountry?.name || 'N/A'
      },
      countries: geoStats.countries
    };
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportData, null, 2));
    const link = document.createElement('a');
    link.setAttribute('href', dataStr);
    link.setAttribute('download', `niiko_origem_geografica_${geoPeriod}_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isImageFormatUrl = (url?: string): boolean => {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    if (lower.includes('.mp4') || lower.includes('.webm') || lower.includes('.mov') || lower.includes('.m4v')) return false;
    return lower.includes('.jpg') || lower.includes('.jpeg') || lower.includes('.png') || lower.includes('.webp') || lower.includes('.gif') || lower.includes('.svg') || lower.includes('unsplash.com') || lower.includes('imgur.com');
  };

  const extractVideoFrameFromFile = (videoFile: File): Promise<File | null> => {
    return new Promise((resolve) => {
      try {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        const url = URL.createObjectURL(videoFile);
        video.src = url;

        let resolved = false;
        const timer = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            URL.revokeObjectURL(url);
            resolve(null);
          }
        }, 6000);

        video.onloadeddata = () => {
          try {
            video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
          } catch (e) {
            if (!resolved) {
              resolved = true;
              clearTimeout(timer);
              URL.revokeObjectURL(url);
              resolve(null);
            }
          }
        };

        video.onseeked = () => {
          if (resolved) return;
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 600;
            canvas.height = video.videoHeight || 800;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              canvas.toBlob((blob) => {
                if (resolved) return;
                resolved = true;
                clearTimeout(timer);
                URL.revokeObjectURL(url);
                if (blob) {
                  const coverFile = new File([blob], `cover_${Date.now()}.jpg`, { type: 'image/jpeg' });
                  resolve(coverFile);
                } else {
                  resolve(null);
                }
              }, 'image/jpeg', 0.92);
            } else {
              resolved = true;
              clearTimeout(timer);
              URL.revokeObjectURL(url);
              resolve(null);
            }
          } catch (err) {
            if (!resolved) {
              resolved = true;
              clearTimeout(timer);
              URL.revokeObjectURL(url);
              resolve(null);
            }
          }
        };

        video.onerror = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timer);
            URL.revokeObjectURL(url);
            resolve(null);
          }
        };
      } catch (e) {
        resolve(null);
      }
    });
  };

  const extractVideoFrameFromUrl = (videoUrl: string): Promise<File | null> => {
    return new Promise((resolve) => {
      try {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.src = videoUrl;

        let resolved = false;
        const timer = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            resolve(null);
          }
        }, 8000);

        video.onloadeddata = () => {
          try {
            video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
          } catch (e) {
            if (!resolved) {
              resolved = true;
              clearTimeout(timer);
              resolve(null);
            }
          }
        };

        video.onseeked = () => {
          if (resolved) return;
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 600;
            canvas.height = video.videoHeight || 800;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              canvas.toBlob((blob) => {
                if (resolved) return;
                resolved = true;
                clearTimeout(timer);
                if (blob) {
                  const coverFile = new File([blob], `cover_${Date.now()}.jpg`, { type: 'image/jpeg' });
                  resolve(coverFile);
                } else {
                  resolve(null);
                }
              }, 'image/jpeg', 0.92);
            } else {
              resolved = true;
              clearTimeout(timer);
              resolve(null);
            }
          } catch (err) {
            if (!resolved) {
              resolved = true;
              clearTimeout(timer);
              resolve(null);
            }
          }
        };

        video.onerror = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timer);
            resolve(null);
          }
        };
      } catch (e) {
        resolve(null);
      }
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      if (file.type.startsWith('video/')) {
        setNewArt(prev => ({ ...prev, isAnimated: true }));
        // Extrai automaticamente um frame JPG para o Google Imagens se nenhuma capa foi selecionada
        if (!coverPendingFile) {
          setIsAutoExtractingCover(true);
          extractVideoFrameFromFile(file).then(frameFile => {
            setIsAutoExtractingCover(false);
            if (frameFile) {
              setCoverPendingFile(frameFile);
              setCoverPreviewUrl(URL.createObjectURL(frameFile));
            }
          }).catch(() => setIsAutoExtractingCover(false));
        }
      } else if (file.type.startsWith('image/')) {
        setNewArt(prev => ({ ...prev, isAnimated: file.type === 'image/gif' }));
        if (!coverPendingFile) {
          setCoverPendingFile(file);
          setCoverPreviewUrl(URL.createObjectURL(file));
        }
      }
    }
  };

  const handleOptimizeMissingCovers = async () => {
    const candidates = arts.filter(a => {
      const img = a.imageUrl || '';
      return !isImageFormatUrl(img) && (a.videoUrl || a.imageUrl);
    });

    if (candidates.length === 0) {
      alert(language === 'pt' 
        ? "Todas as suas obras já possuem imagens de capa válidas em formato de foto (JPG/PNG)! O Google Imagens já consegue indexá-las normalmente." 
        : "All your artworks already have valid JPG/PNG cover images! Google Images can index them normally.");
      return;
    }

    const confirmMsg = language === 'pt'
      ? `Foram encontradas ${candidates.length} obras que estão sem capa em imagem (estão apenas como vídeo). O Google Imagens só indexa fotos (JPG/PNG).\n\nDeseja extrair automaticamente uma capa JPG em alta resolução de cada uma e salvar no servidor agora?`
      : `Found ${candidates.length} artworks without an image cover (currently video only). Google Images only indexes images (JPG/PNG).\n\nDo you want to automatically extract a high-res JPG cover from each video and save to the server now?`;

    if (!confirm(confirmMsg)) return;

    setIsOptimizingCovers(true);
    setOptimizeProgress({ current: 0, total: candidates.length });
    let successCount = 0;

    for (let i = 0; i < candidates.length; i++) {
      const art = candidates[i];
      setOptimizeProgress({ current: i + 1, total: candidates.length });
      const videoSrc = database.resolveMediaUrl(art.videoUrl || art.imageUrl);
      
      try {
        const frameFile = await extractVideoFrameFromUrl(videoSrc);
        if (frameFile) {
          const uploadRes = await database.uploadCoverImage(frameFile);
          if (uploadRes.success && uploadRes.url) {
            const updatedArt: ArtItem = {
              ...art,
              imageUrl: uploadRes.url,
              videoUrl: art.videoUrl || art.imageUrl
            };
            const ok = await database.updateArtMetadata(updatedArt);
            if (ok) {
              onUpdateArts(prev => prev.map(a => a.id === updatedArt.id ? updatedArt : a));
              successCount++;
            }
          }
        }
      } catch (err) {
        console.warn("Erro ao otimizar capa para a obra", art.id, err);
      }
    }

    setIsOptimizingCovers(false);
    setOptimizeProgress(null);

    const finishMsg = language === 'pt'
      ? `Otimização Concluída!\n\n${successCount} de ${candidates.length} obras agora possuem capas JPG prontas para o Google Imagens. Suba os novos arquivos para a Hostinger para acelerar a indexação!`
      : `Optimization Complete!\n\n${successCount} of ${candidates.length} artworks now have JPG covers ready for Google Images. Upload the new files to Hostinger to speed up indexing!`;
    alert(finishMsg);
  };

  const handleQuickExtractCover = async (art: ArtItem) => {
    const videoSrc = database.resolveMediaUrl(art.videoUrl || art.imageUrl);
    try {
      const frameFile = await extractVideoFrameFromUrl(videoSrc);
      if (!frameFile) {
        alert("Não foi possível extrair a imagem do vídeo. Abra a edição da obra e selecione uma capa JPG manualmente.");
        return;
      }
      const uploadRes = await database.uploadCoverImage(frameFile);
      if (uploadRes.success && uploadRes.url) {
        const updatedArt: ArtItem = {
          ...art,
          imageUrl: uploadRes.url,
          videoUrl: art.videoUrl || art.imageUrl
        };
        const ok = await database.updateArtMetadata(updatedArt);
        if (ok) {
          onUpdateArts(prev => prev.map(a => a.id === updatedArt.id ? updatedArt : a));
          alert("✓ Capa JPG extraída e vinculada com sucesso! Esta obra agora está visível para o Google Imagens.");
        }
      } else {
        alert("Erro ao salvar capa no servidor: " + (uploadRes.error || "Erro desconhecido"));
      }
    } catch (e) {
      alert("Falha ao extrair capa: " + e);
    }
  };

  const handleCoverFileSelect = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setCoverPendingFile(file);
      setCoverPreviewUrl(URL.createObjectURL(file));
    } else {
      alert("Por favor, selecione ou arraste um arquivo de imagem (JPG, PNG, GIF, WEBP).");
    }
  };

  const handleEditClick = (art: ArtItem) => {
    setEditingArt(art);
    setCoverPendingFile(null);
    setCoverPreviewUrl(art.imageUrl || null);
    setNewArt({ 
      ...art, 
      imageUrl: art.imageUrl || '',
      subcategory: art.subcategory || (ART_SUBCATEGORIES[art.category]?.[0]?.id || ''),
      artType: art.artType || 'Single Artwork',
      hideSignature: !!art.hideSignature,
      hideDiscord: !!art.hideDiscord,
      hideNotes: !!art.hideNotes,
      hideBuyButton: !!art.hideBuyButton,
      downloadUrl: art.downloadUrl || ''
    });
    setPreviewUrl(art.videoUrl || art.imageUrl);
    setIsAddingArt(true);
    setTimeout(() => {
      if (editFormRef.current) {
        editFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleDeleteArt = async (id: string) => {
    if (!confirm("Excluir esta obra?")) return;
    const success = await database.deleteArt(id);
    if (success) onUpdateArts(prev => prev.filter(a => a.id !== id));
  };

  const handleDeleteOrder = async (id: string) => {
    if (!confirm("Excluir este pedido permanentemente?")) return;
    const success = await database.deleteOrder(id);
    if (success) {
      onUpdateOrders(prev => prev.filter(o => o.id !== id));
      if (expandedOrderId === id) setExpandedOrderId(null);
    }
  };

  const handleDeleteChatThread = async (email: string) => {
    if (!confirm("Excluir conversa com este usuário?")) return;
    const success = await database.deleteChatThread(email);
    if (success) {
      onUpdateChatThreads(prev => prev.filter(t => t.userEmail !== email));
      if (selectedThreadEmail === email) setSelectedThreadEmail(null);
    }
  };

  const handleOpenChatWithUser = (userEmail: string, userName?: string) => {
    if (!userEmail) return;
    setSelectedThreadEmail(userEmail);
    setActiveTab('chats');

    const nameToUse = resolveCustomerDisplayName(userEmail, userName);

    onUpdateChatThreads(prev => {
      const idx = prev.findIndex(t => t.userEmail.toLowerCase() === userEmail.toLowerCase());
      if (idx > -1) {
        const up = [...prev];
        const current = up[idx];
        const isGeneric = !current.userName || ['usuário', 'usuario', 'user', 'cliente'].includes(current.userName.toLowerCase()) || current.userName.includes('@');
        if (isGeneric || (nameToUse && nameToUse !== current.userName)) {
          up[idx] = {
            ...current,
            userName: nameToUse
          };
        }
        return up;
      } else {
        const newThread: ChatThread = {
          userEmail,
          userName: nameToUse,
          messages: [],
          lastUpdated: new Date().toISOString(),
          unreadCount: 0
        };
        return [newThread, ...prev];
      }
    });
  };

  const handleUploadRealArt = async () => {
    setIsUploading(true);

    let finalImageUrl = newArt.imageUrl || '';

    // If coverPendingFile exists, upload it first to get the URL
    if (coverPendingFile) {
      const coverRes = await database.uploadCoverImage(coverPendingFile);
      if (coverRes.success && coverRes.url) {
        finalImageUrl = coverRes.url;
      } else {
        alert("Erro ao enviar a imagem de capa: " + (coverRes.error || "Erro desconhecido"));
        setIsUploading(false);
        return;
      }
    }

    if (editingArt && !pendingFile) {
      const updatedArt = { ...editingArt, ...newArt, imageUrl: finalImageUrl } as ArtItem;
      const success = await database.updateArtMetadata(updatedArt);
      if (success) {
        onUpdateArts(prev => prev.map(a => a.id === updatedArt.id ? updatedArt : a));
        resetForm();
      } else {
        alert("Erro ao atualizar dados no servidor.");
      }
      setIsUploading(false);
      return;
    }

    if (!pendingFile || !newArt.title?.pt) {
      alert("Título (PT) e Arquivo são obrigatórios.");
      setIsUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', pendingFile);
    if (coverPendingFile) {
      formData.append('cover_file', coverPendingFile);
    }
    formData.append('title_pt', newArt.title.pt);
    formData.append('title_en', newArt.title.en || newArt.title.pt);
    formData.append('category', newArt.category || 'Animated');
    formData.append('subcategory', newArt.subcategory || '');
    formData.append('art_type', newArt.artType || 'Single Artwork');
    formData.append('price', (newArt.price || 0).toString());
    formData.append('image_url', finalImageUrl);
    formData.append('cover_url', finalImageUrl);
    formData.append('desc_pt', newArt.description?.pt || '');
    formData.append('desc_en', newArt.description?.en || '');
    formData.append('is_animated', (!!newArt.isAnimated).toString());
    formData.append('steam_background_url', newArt.steamBackgroundUrl || '');
    formData.append('not_for_sale', (!!newArt.notForSale).toString());
    formData.append('stripe_url', newArt.stripeUrl || '');
    formData.append('stripe_url_br', newArt.stripeUrlBr || '');
    formData.append('hide_signature', (!!newArt.hideSignature).toString());
    formData.append('hide_discord', (!!newArt.hideDiscord).toString());
    formData.append('hide_notes', (!!newArt.hideNotes).toString());
    formData.append('hide_buy_button', (!!newArt.hideBuyButton).toString());
    formData.append('aspect_ratio', newArt.aspectRatio || 'vertical');
    formData.append('download_url', newArt.downloadUrl || '');

    const result = await database.uploadNewArt(formData);
    if (result.success && result.item) {
      if (editingArt) {
        await database.deleteArt(editingArt.id);
        onUpdateArts(prev => [result.item!, ...prev.filter(a => a.id !== editingArt.id)]);
      } else {
        onUpdateArts(prev => [result.item!, ...prev]);
      }
      resetForm();
    } else {
      alert("Erro no upload: " + result.error);
    }
    setIsUploading(false);
  };

  const handleSaveCategoryCover = async () => {
    if (!editingCategory) return;
    setIsSyncing(true);
    let finalUrl = catCoverPreview || '';
    if (catPendingFile) {
      const uploadRes = await database.uploadCategoryCover(editingCategory, catPendingFile);
      if (uploadRes.success && uploadRes.url) {
        finalUrl = uploadRes.url;
      } else {
        alert("Erro no upload da capa: " + (uploadRes.error || "Erro desconhecido"));
        setIsSyncing(false);
        return;
      }
    }
    const updatedCovers = { ...categoryCovers, [editingCategory]: finalUrl };
    onUpdateCategoryCovers(updatedCovers);
    const success = await database.saveCategoryCovers(updatedCovers);
    if (success) {
      setEditingCategory(null);
      setCatPendingFile(null);
      setCatCoverPreview(null);
    }
    setIsSyncing(false);
  };

  const resetForm = () => {
    setIsAddingArt(false);
    setEditingArt(null);
    setPendingFile(null);
    setPreviewUrl(null);
    setCoverPendingFile(null);
    setCoverPreviewUrl(null);
    setIsDragOverCover(false);
    setNewArt({ 
      title: { pt: '', en: '' }, 
      category: ArtCategory.ANIMATED, 
      artType: 'Single Artwork',
      price: 15.00, 
      isAnimated: true,
      steamBackgroundUrl: '',
      notForSale: false,
      hideSignature: false,
      hideDiscord: false,
      hideNotes: false,
      hideBuyButton: false,
      downloadUrl: ''
    });
  };

  const maxVisits = Math.max(...analytics.map(a => a.uniqueCount || a.count), 1);

  return (
    <div className="fixed inset-0 z-[300] bg-[#050505] overflow-y-auto pb-20 custom-scrollbar">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex justify-between items-center mb-16 border-b border-white/5 pb-10">
          <div className="flex items-center gap-4">
            <div className="bg-white p-2">
              <Flower className="w-6 h-6 text-black" />
            </div>
            <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Santuário <span className="text-[#c084fc]">Admin</span></h2>
          </div>
          <div className="flex bg-zinc-900 p-1 flex-wrap gap-0.5">
            {[
              { id: 'gallery', label: language === 'en' ? 'Gallery' : language === 'es' ? 'Galería' : language === 'ru' ? 'Галерея' : 'Galeria' },
              { id: 'orders', label: language === 'en' ? 'Orders' : language === 'es' ? 'Pedidos' : language === 'ru' ? 'Заказы' : 'Pedidos' },
              { id: 'users', label: language === 'en' ? 'Users & Sales' : language === 'es' ? 'Usuarios & Ventas' : language === 'ru' ? 'Пользователи и Продажи' : 'Usuários & Vendas' },
              { id: 'chats', label: language === 'en' ? 'Live Chat' : language === 'es' ? 'Chat' : language === 'ru' ? 'Чат' : 'Atendimento' },
              { id: 'categories', label: language === 'en' ? 'Covers' : language === 'es' ? 'Portadas' : language === 'ru' ? 'Обложки' : 'Capas' },
              { id: 'commissions', label: 'Custom Art' },
              { id: 'reviews', label: language === 'en' ? 'Reviews' : language === 'es' ? 'Reseñas' : language === 'ru' ? 'Отзывы' : 'Avaliações' },
              { id: 'screenshots', label: language === 'en' ? 'Screenshots' : language === 'es' ? 'Capturas' : language === 'ru' ? 'Скриншоты' : 'Screenshots de Perfis' },
              { id: 'stats', label: language === 'en' ? 'Stats' : language === 'es' ? 'Estadísticas' : language === 'ru' ? 'Статистика' : 'Estatísticas' },
              { id: 'settings', label: language === 'en' ? 'Settings' : language === 'es' ? 'Ajustes' : language === 'ru' ? 'Настройки' : 'Ajustes' },
              { id: 'footer', label: language === 'en' ? 'Footer' : language === 'es' ? 'Pie' : language === 'ru' ? 'Подвал' : 'Rodapé' },
              { id: 'faq', label: 'F.A.Q.' },
            ].map(({ id, label }) => (
              <button 
                key={id} 
                onClick={() => setActiveTab(id as any)} 
                className={`px-5 py-3 text-[10px] font-black uppercase transition-all relative ${activeTab === id ? 'bg-[#c084fc] text-white shadow-lg' : 'text-zinc-500 hover:text-white hover:bg-zinc-800/50'}`}
              >
                {label}
                {id === 'orders' && notifications && notifications.orders > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded-full animate-bounce">
                    {notifications.orders}
                  </span>
                )}
                {id === 'chats' && notifications && notifications.chats > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded-full animate-bounce">
                    {notifications.chats}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="p-4 bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white"><X className="w-7 h-7" /></button>
        </div>

        {activeTab === 'commissions' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            {/* Custom Art Introduction Text Editor */}
            <div className="bg-zinc-900/60 border border-white/10 p-6 md:p-8 space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/5 pb-4">
                <div>
                  <h3 className="text-white font-black uppercase text-sm tracking-widest flex items-center gap-2">
                    <Type className="w-4 h-4 text-[#c084fc]" />
                    {language === 'en' ? 'Custom Art Introduction Text' : 'Texto de Introdução (Custom Art)'}
                  </h3>
                  <p className="text-zinc-500 text-[9px] font-black uppercase tracking-widest mt-1">
                    {language === 'en' 
                      ? 'Phrase displayed below the title on the Custom Art / Commissions page' 
                      : 'Frase de apresentação exibida logo abaixo do título na página Custom Art'}
                  </p>
                </div>

                {/* Language Switcher Tabs */}
                <div className="flex items-center gap-1 bg-black/60 p-1 border border-white/10">
                  {[
                    { id: 'pt', label: '🇧🇷 PT', name: 'Português' },
                    { id: 'en', label: '🇺🇸 EN', name: 'English' },
                    { id: 'es', label: '🇪🇸 ES', name: 'Español' },
                    { id: 'ru', label: '🇷🇺 RU', name: 'Русский' }
                  ].map(langItem => (
                    <button
                      key={langItem.id}
                      type="button"
                      onClick={() => setCustomIntroLang(langItem.id as any)}
                      className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                        customIntroLang === langItem.id
                          ? 'bg-[#c084fc] text-white shadow-md'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800/60'
                      }`}
                    >
                      <span>{langItem.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Active Language Editor */}
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="text-[10px] font-black text-zinc-300 uppercase tracking-widest flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5 text-[#c084fc]" />
                    {customIntroLang === 'pt' && 'Texto em Português (PT)'}
                    {customIntroLang === 'en' && 'Text in English (EN)'}
                    {customIntroLang === 'es' && 'Texto en Español (ES)'}
                    {customIntroLang === 'ru' && 'Текст на Русском (RU)'}
                  </label>

                  <button
                    type="button"
                    onClick={() => {
                      const currentVal = (
                        customIntroLang === 'pt' ? siteSettingsState.customRequestIntro_pt :
                        customIntroLang === 'en' ? siteSettingsState.customRequestIntro_en :
                        customIntroLang === 'es' ? siteSettingsState.customRequestIntro_es :
                        siteSettingsState.customRequestIntro_ru
                      ) || '';
                      
                      if (!currentVal.trim()) {
                        alert(language === 'en' ? 'Please type a text first.' : 'Digite um texto primeiro antes de copiar.');
                        return;
                      }

                      setSiteSettingsState(prev => ({
                        ...prev,
                        customRequestIntro_pt: currentVal,
                        customRequestIntro_en: currentVal,
                        customRequestIntro_es: currentVal,
                        customRequestIntro_ru: currentVal,
                      }));

                      alert(language === 'en' ? 'Text replicated to all languages (PT, EN, ES, RU)! Remember to click Save.' : 'Texto replicado para todos os idiomas (PT, EN, ES, RU)! Lembre-se de clicar no botão "Salvar".');
                    }}
                    className="text-[9px] font-black uppercase text-[#c084fc] hover:text-white hover:underline flex items-center gap-1.5 transition-colors cursor-pointer"
                    title="Copia o texto deste idioma para PT, EN, ES e RU"
                  >
                    <Copy className="w-3 h-3" />
                    {language === 'en' ? 'Replicate this text to all languages' : 'Copiar este texto para todos os idiomas'}
                  </button>
                </div>

                <textarea
                  rows={3}
                  value={
                    customIntroLang === 'pt' ? (siteSettingsState.customRequestIntro_pt ?? '') :
                    customIntroLang === 'en' ? (siteSettingsState.customRequestIntro_en ?? '') :
                    customIntroLang === 'es' ? (siteSettingsState.customRequestIntro_es ?? '') :
                    (siteSettingsState.customRequestIntro_ru ?? '')
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setSiteSettingsState(prev => {
                      if (customIntroLang === 'pt') return { ...prev, customRequestIntro_pt: val };
                      if (customIntroLang === 'en') return { ...prev, customRequestIntro_en: val };
                      if (customIntroLang === 'es') return { ...prev, customRequestIntro_es: val };
                      return { ...prev, customRequestIntro_ru: val };
                    });
                  }}
                  placeholder={
                    customIntroLang === 'pt' ? "Digite a frase de introdução em Português..." :
                    customIntroLang === 'en' ? "Type the introduction quote in English..." :
                    customIntroLang === 'es' ? "Escribe la frase de introducción en Español..." :
                    "Введите вводный текст на русском языке..."
                  }
                  className="w-full bg-black border border-zinc-700 focus:border-[#c084fc] p-4 text-white text-xs outline-none leading-relaxed transition-all resize-none shadow-inner"
                />

                {/* Preview Box */}
                <div className="p-3 bg-black/40 border border-white/5 flex items-start gap-3">
                  <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-widest mt-0.5">Preview:</span>
                  <p className="text-zinc-400 text-xs italic">
                    "{
                      (
                        customIntroLang === 'pt' ? siteSettingsState.customRequestIntro_pt :
                        customIntroLang === 'en' ? siteSettingsState.customRequestIntro_en :
                        customIntroLang === 'es' ? siteSettingsState.customRequestIntro_es :
                        siteSettingsState.customRequestIntro_ru
                      ) || (language === 'en' ? 'Looking for something beyond the common gallery? Contact our master craftsmen...' : 'Deseja algo que transcenda a galeria comum? Entre em contato...')
                    }"
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={async () => {
                      setIsSyncing(true);
                      onUpdateSiteSettings(siteSettingsState);
                      const saveRes = await database.saveSettings(siteSettingsState);
                      if (saveRes.success) {
                        alert(language === 'en' ? "Custom Art introduction text saved successfully!" : "Texto de introdução da Custom Art salvo com sucesso!");
                      } else {
                        alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                      }
                      setIsSyncing(false);
                    }}
                    disabled={isSyncing}
                    className="w-full sm:w-auto px-8 py-3 bg-[#c084fc] hover:bg-white text-white hover:text-black font-black uppercase text-[10px] tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg disabled:opacity-40"
                  >
                    {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {language === 'en' ? 'Save Introduction Text' : 'Salvar Texto de Introdução'}
                  </button>

                  <span className="text-[9px] text-zinc-500">
                    {language === 'en' ? 'Saved texts appear immediately on the Custom Art tab.' : 'As alterações aparecem imediatamente na aba Custom Art.'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mb-8 border-t border-white/5 pt-8">
              <div>
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Capas de Custom Art</h3>
                <p className="text-zinc-600 text-[8px] font-black uppercase tracking-[0.4em]">Edite a demonstração visual das animações</p>
              </div>
              <button 
                onClick={refreshCommissionTiers}
                className="p-3 bg-zinc-950 text-[#c084fc] hover:bg-[#c084fc] hover:text-white transition-all flex items-center gap-2 text-[10px] font-black uppercase"
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} /> Atualizar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {commissionTiers.map(tier => (
                <div key={tier.id} className="bg-zinc-900/40 border border-white/5 p-6 space-y-6 group hover:border-[#c084fc]/30 transition-all">
                  <div className="aspect-[9/14] bg-black overflow-hidden relative">
                    {tierPreview && editingTier === tier.id ? (
                      isVideoUrl(tierPreview) ? (
                        <video src={tierPreview} autoPlay loop muted className="w-full h-full object-cover" />
                      ) : (
                        <img src={tierPreview} className="w-full h-full object-cover" />
                      )
                    ) : (
                      isVideoUrl(tier.demoUrl) ? (
                        <video src={tier.demoUrl} autoPlay loop muted className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                      ) : (
                        <img src={tier.demoUrl} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                      )
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <h4 className="text-white font-black uppercase text-xs tracking-widest">{tier.title}</h4>
                    </div>
                  </div>

                  {editingTier === tier.id ? (
                    <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">URL da Demonstração</label>
                          <input 
                            type="text" 
                            placeholder="https://..." 
                            value={tierPreview || ''} 
                            onChange={(e) => setTierPreview(e.target.value)} 
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[10px] outline-none focus:border-[#c084fc]" 
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Preço Inicial ($)</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input 
                              type="number" 
                              step="0.01"
                              value={commissionTiers.find(t => t.id === tier.id)?.price || 0} 
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, price: val } : t));
                              }} 
                              className="w-full bg-black border border-zinc-800 p-3 pl-8 text-white text-[10px] outline-none focus:border-[#c084fc]" 
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Preço Máximo ($ - Opcional)</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input 
                              type="number" 
                              step="0.01"
                              placeholder="Fim da faixa"
                              value={commissionTiers.find(t => t.id === tier.id)?.maxPrice || ''} 
                              onChange={(e) => {
                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, maxPrice: val } : t));
                              }} 
                              className="w-full bg-black border border-zinc-800 p-3 pl-8 text-white text-[10px] outline-none focus:border-[#c084fc]" 
                            />
                          </div>
                        </div>
                        <div className="space-y-2 col-span-full">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Descrição do Plano</label>
                          <textarea 
                            rows={2}
                            value={commissionTiers.find(t => t.id === tier.id)?.description || ''} 
                            onChange={(e) => {
                              const val = e.target.value;
                              setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, description: val } : t));
                            }} 
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[10px] outline-none focus:border-[#c084fc] resize-none" 
                            placeholder="Descreva este plano..."
                          />
                        </div>
                        <div className="space-y-2 col-span-full">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Link de Pagamento Stripe - USD/Geral (Opcional)</label>
                          <div className="relative">
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input 
                              type="text" 
                              placeholder="https://buy.stripe.com/..." 
                              value={commissionTiers.find(t => t.id === tier.id)?.stripeUrl || ''} 
                              onChange={(e) => {
                                const val = e.target.value;
                                setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, stripeUrl: val } : t));
                              }} 
                              className="w-full bg-black border border-zinc-800 p-3 pl-8 text-white text-[10px] outline-none focus:border-[#c084fc]" 
                            />
                          </div>
                        </div>
                        <div className="space-y-2 col-span-full">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Link de Pagamento Stripe - BRL/Brasil (Opcional)</label>
                          <div className="relative">
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input 
                              type="text" 
                              placeholder="https://buy.stripe.com/..." 
                              value={commissionTiers.find(t => t.id === tier.id)?.stripeUrlBr || ''} 
                              onChange={(e) => {
                                const val = e.target.value;
                                setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, stripeUrlBr: val } : t));
                              }} 
                              className="w-full bg-black border border-zinc-800 p-3 pl-8 text-white text-[10px] outline-none focus:border-[#c084fc]" 
                            />
                          </div>
                        </div>
                      </div>
                      <input 
                        ref={tierFileInputRef} 
                        type="file" 
                        className="hidden" 
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setTierPendingFile(file);
                            setTierPreview(URL.createObjectURL(file));
                          }
                        }} 
                        accept="image/*,video/mp4" 
                      />
                      <div className="flex gap-2">
                        <button 
                          onClick={() => tierFileInputRef.current?.click()} 
                          className="flex-1 bg-zinc-800 text-white py-3 text-[9px] font-black uppercase hover:bg-zinc-700 transition-all flex items-center justify-center gap-2"
                        >
                          <Upload className="w-3 h-3" /> Upload
                        </button>
                        <button 
                          onClick={async () => {
                            setIsSyncing(true);
                            let finalUrl = tierPreview || tier.demoUrl;
                            if (tierPendingFile) {
                              const uploadRes = await database.uploadCommissionTierCover(tier.id, tierPendingFile);
                              if (uploadRes.success) {
                                finalUrl = uploadRes.url!;
                              } else {
                                alert(uploadRes.error);
                                setIsSyncing(false);
                                return;
                              }
                            }
                            
                            const currentTier = commissionTiers.find(t => t.id === tier.id);
                            const updatedTiers = commissionTiers.map(t => t.id === tier.id ? { 
                              ...t, 
                              demoUrl: finalUrl, 
                              price: currentTier?.price || 0,
                              maxPrice: currentTier?.maxPrice
                            } : t);
                            const saveRes = await database.saveCommissionTiers(updatedTiers);
                            if (saveRes.success) {
                              setCommissionTiers(updatedTiers);
                              onUpdateCommissionTiers(updatedTiers);
                              setEditingTier(null);
                              setTierPendingFile(null);
                              setTierPreview(null);
                            } else {
                              alert(`Erro ao salvar no servidor: ${saveRes.error || 'Desconhecido'}`);
                            }
                            setIsSyncing(false);
                          }} 
                          disabled={isSyncing} 
                          className="flex-1 bg-[#c084fc] text-white py-3 text-[9px] font-black uppercase hover:bg-white hover:text-[#c084fc] transition-all flex items-center justify-center gap-2"
                        >
                          {isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Salvar
                        </button>
                        <button 
                          onClick={() => { setEditingTier(null); setTierPendingFile(null); setTierPreview(null); }} 
                          className="px-4 bg-zinc-950 text-zinc-500 py-3 text-[9px] font-black uppercase hover:text-white"
                        >
                          X
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button 
                      onClick={() => { setEditingTier(tier.id); setTierPreview(tier.demoUrl); }} 
                      className="w-full py-4 border border-zinc-800 text-zinc-500 font-black uppercase text-[10px] hover:text-white hover:border-[#c084fc] transition-all flex items-center justify-center gap-2"
                    >
                      <Edit2 className="w-3 h-3" /> Editar Plano
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            {/* Top 4 KPI Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-zinc-900/40 border border-white/5 p-6 md:p-8 backdrop-blur-md relative overflow-hidden group hover:border-[#c084fc]/30 transition-all">
                <div className="flex justify-between items-center mb-4">
                  <div className="p-2.5 bg-[#c084fc]/10 text-[#c084fc] border border-[#c084fc]/20">
                    <Users className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Únicos Hoje</span>
                </div>
                <div className="text-3xl md:text-4xl font-black text-white tracking-tight">
                  {todayStats.uniqueCount}
                </div>
                <p className="text-[8px] text-zinc-500 font-black uppercase mt-2 tracking-widest flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c084fc] animate-pulse"></span>
                  Visitantes distintos hoje
                </p>
              </div>

              <div className="bg-zinc-900/40 border border-white/5 p-6 md:p-8 backdrop-blur-md relative overflow-hidden group hover:border-green-500/30 transition-all">
                <div className="flex justify-between items-center mb-4">
                  <div className="p-2.5 bg-green-500/10 text-green-400 border border-green-500/20">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Total de Cliques</span>
                </div>
                <div className="text-3xl md:text-4xl font-black text-white tracking-tight">
                  {todayStats.totalCount}
                </div>
                <p className="text-[8px] text-zinc-500 font-black uppercase mt-2 tracking-widest flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                  Carregamentos de página (F5)
                </p>
              </div>

              <div className="bg-zinc-900/40 border border-white/5 p-6 md:p-8 backdrop-blur-md relative overflow-hidden group hover:border-blue-500/30 transition-all">
                <div className="flex justify-between items-center mb-4">
                  <div className="p-2.5 bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">
                    {geoPeriod === 'today' ? 'Únicos Hoje' : geoPeriod === '7days' ? 'Únicos (7 Dias)' : geoPeriod === '30days' ? 'Únicos (30 Dias)' : 'Total no Histórico'}
                  </span>
                </div>
                <div className="text-3xl md:text-4xl font-black text-white tracking-tight">
                  {geoStats.totalUniqueVisitors}
                </div>
                <p className="text-[8px] text-zinc-500 font-black uppercase mt-2 tracking-widest flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  {geoStats.totalPageHits} acessos no período
                </p>
              </div>

              <div className="bg-zinc-900/40 border border-white/5 p-6 md:p-8 backdrop-blur-md relative overflow-hidden group hover:border-purple-500/30 transition-all">
                <div className="flex justify-between items-center mb-4">
                  <div className="p-2.5 bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    <Globe className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Países Ativos</span>
                </div>
                <div className="text-3xl md:text-4xl font-black text-white tracking-tight flex items-baseline gap-2">
                  <span>{geoStats.countries.length}</span>
                  <span className="text-xs text-zinc-500 font-normal uppercase">
                    {geoStats.countries.length === 1 ? 'País' : 'Países'}
                  </span>
                </div>
                <p className="text-[8px] text-zinc-500 font-black uppercase mt-2 tracking-widest truncate">
                  {geoStats.topCountry ? (
                    <span className="text-purple-300">
                      Líder: {geoStats.topCountry.flag} {geoStats.topCountry.name} ({geoStats.topCountry.uniquePercentage.toFixed(0)}%)
                    </span>
                  ) : (
                    'Rastreamento em tempo real'
                  )}
                </p>
              </div>
            </div>

            {/* Daily Visits Chart */}
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
                <div>
                  <h3 className="text-white font-black uppercase text-sm tracking-widest flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#c084fc]" />
                    Fluxo de Presença Diário
                  </h3>
                  <p className="text-zinc-500 text-[9px] font-black uppercase tracking-widest mt-1">
                    Histórico de tráfego por dia em horário de Brasília (UTC-3)
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button 
                    onClick={handleTestVisit} 
                    className="px-4 py-2.5 bg-[#c084fc]/10 border border-[#c084fc]/30 text-[#c084fc] hover:bg-[#c084fc] hover:text-black transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest"
                    title="Envia um registro de visita imediato com sua localização atual para testar a comunicação com a Hostinger"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    Registrar Visita de Teste
                  </button>
                  <button 
                    onClick={refreshAnalytics} 
                    className="px-4 py-2.5 bg-zinc-950 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                    Atualizar Dados
                  </button>
                </div>
              </div>
              
              <div className="h-64 flex items-end gap-2 md:gap-3 overflow-x-auto pb-4 custom-scrollbar">
                {analytics.length === 0 ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-center p-6 bg-zinc-950/40 border border-dashed border-white/5">
                    <p className="text-zinc-400 text-xs font-black uppercase tracking-widest">
                      Nenhuma visita registrada no servidor ainda
                    </p>
                    <p className="text-zinc-500 text-[10px] max-w-md">
                      Quando visitantes acessam seu site na Hostinger, o tráfego e as localizações são contabilizados automaticamente. Você também pode testar agora mesmo:
                    </p>
                    <button
                      onClick={handleTestVisit}
                      className="px-5 py-2.5 bg-[#c084fc] text-black font-black text-xs uppercase tracking-widest hover:bg-purple-300 transition-all flex items-center gap-2"
                    >
                      <Globe className="w-4 h-4" />
                      Registrar Meu Acesso Agora
                    </button>
                  </div>
                ) : (
                  analytics.map((day, idx) => {
                    const displayCount = day.uniqueCount || day.count || 0;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center group min-w-[32px]">
                        <div className="text-[8px] font-black text-white mb-2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center bg-zinc-950/90 border border-white/10 px-2 py-1 pointer-events-none z-10 shadow-lg">
                          <span className="text-[#c084fc]">Únicos: {displayCount}</span>
                          <span className="text-zinc-400">Total: {day.totalCount}</span>
                        </div>
                        <div 
                          className="w-full bg-[#c084fc]/10 border-t border-x border-[#c084fc]/30 group-hover:bg-[#c084fc] transition-all relative overflow-hidden" 
                          style={{ height: `${Math.max((displayCount / maxVisits) * 100, 4)}%` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        </div>
                        <div className="text-[7px] font-black text-zinc-600 uppercase mt-4 rotate-45 md:rotate-0 text-center whitespace-nowrap">
                          {day.date.split('-').slice(1).reverse().join('/')}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Geographic Origin Section */}
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md space-y-8">
              {/* Header with Period Tabs and Export Buttons */}
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 border-b border-white/5 pb-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#c084fc]/10 border border-[#c084fc]/30 text-[#c084fc] mt-1">
                    <Globe className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-white font-black uppercase text-base tracking-widest flex items-center gap-2">
                      Origem Geográfica dos Visitantes
                    </h3>
                    <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest mt-1">
                      Mapeamento de países, estados e regiões de onde chegam os visitantes do site
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                  {/* Period Filter Tabs */}
                  <div className="flex bg-zinc-950 p-1 border border-white/5">
                    {[
                      { id: 'today', label: 'Hoje' },
                      { id: '7days', label: '7 Dias' },
                      { id: '30days', label: '30 Dias' },
                      { id: 'all', label: 'Histórico Completo' }
                    ].map(period => (
                      <button
                        key={period.id}
                        onClick={() => setGeoPeriod(period.id as any)}
                        className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-wider transition-all ${
                          geoPeriod === period.id
                            ? 'bg-[#c084fc] text-white shadow'
                            : 'text-zinc-500 hover:text-white hover:bg-zinc-900'
                        }`}
                      >
                        {period.label}
                      </button>
                    ))}
                  </div>

                  {/* Export Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportGeoCSV}
                      title="Exportar dados geográficos em CSV"
                      className="px-3 py-2 bg-zinc-950 border border-zinc-800 text-zinc-300 hover:text-white hover:border-[#c084fc] text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-green-400" />
                      CSV
                    </button>
                    <button
                      onClick={handleExportGeoJSON}
                      title="Exportar dados geográficos em JSON"
                      className="px-3 py-2 bg-zinc-950 border border-zinc-800 text-zinc-300 hover:text-white hover:border-[#c084fc] text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all"
                    >
                      <Download className="w-3.5 h-3.5 text-blue-400" />
                      JSON
                    </button>
                  </div>
                </div>
              </div>

              {/* Search & Sort Toolbar */}
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 bg-zinc-950/60 p-4 border border-white/5">
                <div className="relative flex-1 max-w-md">
                  <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={geoSearchQuery}
                    onChange={e => setGeoSearchQuery(e.target.value)}
                    placeholder="Filtrar por país, estado ou código (ex: Brasil, SP, US)..."
                    className="w-full bg-zinc-900 border border-white/10 text-white pl-10 pr-4 py-2 text-xs placeholder:text-zinc-600 focus:border-[#c084fc] outline-none"
                  />
                  {geoSearchQuery && (
                    <button
                      onClick={() => setGeoSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white text-xs"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-[9px] font-black text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                    Ordenar por:
                  </span>
                  <select
                    value={geoSortBy}
                    onChange={e => setGeoSortBy(e.target.value as any)}
                    className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-xs uppercase font-bold outline-none focus:border-[#c084fc]"
                  >
                    <option value="visitors_desc">Mais Visitantes Únicos</option>
                    <option value="hits_desc">Mais Cliques / Pageviews</option>
                    <option value="name_asc">Nome do País (A-Z)</option>
                  </select>
                </div>
              </div>

              {/* Geographic Cards & Region Expansion List */}
              {filteredGeoCountries.length === 0 ? (
                <div className="py-16 text-center border border-dashed border-white/10 bg-zinc-950/40">
                  <Globe className="w-10 h-10 text-zinc-700 mx-auto mb-3 animate-pulse" />
                  <p className="text-zinc-400 font-bold uppercase text-xs tracking-widest">
                    {geoSearchQuery
                      ? 'Nenhum país ou região corresponde à sua busca.'
                      : 'Ainda não há dados geográficos suficientes registrados neste período.'}
                  </p>
                  <p className="text-zinc-600 text-[10px] uppercase tracking-widest mt-1">
                    Conforme novos visitantes acessam a loja, seus países e regiões serão listados aqui automaticamente.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredGeoCountries.map(country => {
                    const isExpanded = expandedCountryCode === country.code;
                    const hasRegions = country.regions && country.regions.length > 0;

                    return (
                      <div
                        key={country.code}
                        className={`border transition-all duration-300 ${
                          isExpanded
                            ? 'bg-zinc-950/80 border-[#c084fc]/50 shadow-lg shadow-[#c084fc]/5'
                            : 'bg-zinc-950/40 border-white/5 hover:border-white/20'
                        }`}
                      >
                        {/* Main Country Row */}
                        <div className="p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                          {/* Country Identification */}
                          <div className="flex items-center gap-4 min-w-[220px]">
                            <div className="text-3xl filter drop-shadow select-none">
                              {country.flag}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm md:text-base font-black text-white uppercase tracking-wider">
                                  {country.name}
                                </span>
                                <span className="px-1.5 py-0.5 bg-zinc-900 border border-white/10 text-zinc-400 text-[9px] font-mono font-bold uppercase">
                                  {country.code}
                                </span>
                              </div>
                              <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest mt-0.5">
                                {hasRegions ? `${country.regions.length} ${country.regions.length === 1 ? 'região / estado' : 'regiões / estados'}` : 'Localização principal'}
                              </p>
                            </div>
                          </div>

                          {/* Progress Visual Bar */}
                          <div className="flex-1 w-full md:max-w-xs space-y-1.5">
                            <div className="flex justify-between text-[9px] font-black uppercase text-zinc-400">
                              <span>Participação no Tráfego</span>
                              <span className="text-[#c084fc] font-bold">{country.uniquePercentage.toFixed(1)}%</span>
                            </div>
                            <div className="h-2 w-full bg-zinc-900 overflow-hidden border border-white/5">
                              <div
                                className="h-full bg-gradient-to-r from-[#c084fc]/70 to-[#c084fc] transition-all duration-500"
                                style={{ width: `${Math.max(country.uniquePercentage, 2)}%` }}
                              />
                            </div>
                          </div>

                          {/* Statistics Badges */}
                          <div className="flex items-center gap-4 sm:gap-6 self-stretch md:self-auto justify-between md:justify-end">
                            <div className="text-left md:text-right">
                              <div className="text-xs font-black text-white flex items-center md:justify-end gap-1.5">
                                <Users className="w-3 h-3 text-[#c084fc]" />
                                <span>{country.uniqueCount}</span>
                                <span className="text-[9px] text-zinc-500 font-normal uppercase">únicos</span>
                              </div>
                              <div className="text-[9px] text-zinc-500 uppercase tracking-widest mt-0.5">
                                {country.totalCount} {country.totalCount === 1 ? 'clique' : 'cliques'}
                              </div>
                            </div>

                            {/* Accordion Expand Button */}
                            {hasRegions && (
                              <button
                                onClick={() => setExpandedCountryCode(isExpanded ? null : country.code)}
                                className={`px-3 py-2 text-[9px] font-black uppercase tracking-widest border transition-all flex items-center gap-1.5 ${
                                  isExpanded
                                    ? 'bg-[#c084fc] text-white border-[#c084fc]'
                                    : 'bg-zinc-900 text-zinc-400 border-white/10 hover:text-white hover:border-[#c084fc]'
                                }`}
                              >
                                <MapPin className="w-3 h-3" />
                                <span>{isExpanded ? 'Ocultar' : 'Ver Regiões'}</span>
                                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Detailed Sub-Regions Breakdown */}
                        {isExpanded && hasRegions && (
                          <div className="border-t border-white/5 bg-black/40 p-5 md:p-6 space-y-4 animate-in fade-in duration-300">
                            <div className="flex justify-between items-center">
                              <span className="text-[9px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
                                <Navigation className="w-3 h-3 text-[#c084fc]" />
                                Detalhamento por Estados e Regiões de {country.name}
                              </span>
                              <span className="text-[9px] font-mono text-zinc-600">
                                {country.regions.length} {country.regions.length === 1 ? 'área' : 'áreas'} detectadas
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {country.regions.map((region, rIdx) => (
                                <div
                                  key={rIdx}
                                  className="bg-zinc-900/60 border border-white/5 p-3.5 hover:border-white/15 transition-all space-y-2"
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-2">
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#c084fc]"></span>
                                      <span className="text-xs font-bold text-white uppercase tracking-wider">
                                        {region.name}
                                      </span>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-xs font-black text-[#c084fc]">
                                        {region.uniqueCount}
                                      </span>
                                      <span className="text-[9px] text-zinc-500 ml-1 uppercase">únicos</span>
                                    </div>
                                  </div>

                                  <div className="space-y-1">
                                    <div className="h-1.5 w-full bg-zinc-950 overflow-hidden">
                                      <div
                                        className="h-full bg-[#c084fc]/70 transition-all duration-300"
                                        style={{ width: `${Math.max(region.uniquePercentage, 3)}%` }}
                                      />
                                    </div>
                                    <div className="flex justify-between text-[8px] font-black text-zinc-500 uppercase tracking-widest">
                                      <span>{region.uniquePercentage.toFixed(1)}% do tráfego de {country.code}</span>
                                      <span>{region.totalCount} pageviews</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex justify-between items-center mb-10 border-b border-white/5 pb-6">
                <div className="flex items-center gap-4">
                  <Star className="w-6 h-6 text-[#c084fc] fill-[#c084fc]" />
                  <div>
                    <h3 className="text-white font-black uppercase text-sm tracking-widest">{language === 'pt' ? 'Gerenciamento de Avaliações' : 'Reviews & Feedback Management'}</h3>
                    <p className="text-zinc-600 text-[8px] font-black uppercase tracking-[0.4em]">{language === 'pt' ? 'Responda ou exclua depoimentos de clientes reais' : 'Reply to or delete verified customer feedback'}</p>
                  </div>
                </div>
                <div className="bg-zinc-950 px-4 py-2 border border-white/5 font-mono text-xs text-zinc-400">
                  <span className="text-[#c084fc] font-black">{cleanComments.length}</span> {language === 'pt' ? 'avaliações no total' : 'total reviews'}
                </div>
              </div>

              {cleanComments.length === 0 ? (
                <div className="py-24 text-center border border-dashed border-white/5">
                  <Star className="w-12 h-12 text-zinc-700 mx-auto mb-4 opacity-20" />
                  <p className="text-zinc-500 font-bold uppercase tracking-wider text-[10px]">
                    {language === 'pt' ? 'Nenhuma avaliação registrada no acervo.' : 'No registered reviews in cache.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {cleanComments.map((comment) => (
                    <div key={comment.id} className="bg-black/40 border border-white/5 p-6 md:p-8 space-y-6 relative group transition-all hover:border-zinc-800">
                      
                      {/* Top bar of comment */}
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-white/5 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center font-black text-xs text-[#c084fc]">
                            {comment.userName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-black uppercase text-xs tracking-wider">{comment.userName}</span>
                              <span className="text-zinc-500 font-mono text-[9px]">({comment.userEmail})</span>
                              {comment.verified && (
                                <span className="text-[7px] font-black text-[#c084fc] uppercase bg-[#c084fc]/10 px-2 py-0.5 rounded-full border border-[#c084fc]/10">
                                  {language === 'pt' ? 'Cliente Real' : 'Real Customer'}
                                </span>
                              )}
                            </div>
                            <span className="text-[9px] text-zinc-600 font-mono block mt-1">{comment.date} | ID: {comment.id}</span>
                          </div>
                        </div>

                        {/* Stars */}
                        <div className="flex items-center gap-1 bg-black/80 px-3 py-1.5 border border-white/5">
                          {[1, 2, 3, 4, 5].map((starValue) => (
                            <Star 
                              key={starValue} 
                              className={`w-3.5 h-3.5 ${starValue <= (comment.rating || 5) ? 'text-[#c084fc] fill-[#c084fc]' : 'text-zinc-800'}`} 
                            />
                          ))}
                        </div>
                      </div>

                      {/* Comment text */}
                      <div className="text-sm font-medium text-zinc-300 italic py-2 pl-4 border-l-2 border-zinc-900">
                        "{comment.text}"
                      </div>

                      {/* Existing Reply */}
                      {comment.reply ? (
                        <div className="mt-4 bg-[#c084fc]/5 border border-[#c084fc]/10 p-5 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-[8px] font-black uppercase tracking-widest text-[#c084fc] flex items-center gap-2">
                              <span>★</span> {language === 'pt' ? 'Sua Resposta Oficial' : 'Your Official Reply'}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteReply(comment.id)}
                              className="text-red-400 hover:text-red-300 font-black text-[9px] uppercase tracking-wider flex items-center gap-1 transition-colors hover:underline"
                            >
                              <Trash2 className="w-3 h-3" /> {language === 'pt' ? 'Deletar Resposta' : 'Delete Reply'}
                            </button>
                          </div>
                          <p className="text-xs text-zinc-400 leading-relaxed font-medium">
                            {comment.reply}
                          </p>
                        </div>
                      ) : (
                        /* Unreplied Form */
                        <div className="space-y-3 pt-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block font-mono">
                            {language === 'pt' ? 'Responder Avaliação' : 'Reply to Review'}
                          </label>
                          <div className="flex gap-4">
                            <input 
                              type="text"
                              value={commentReplyTexts[comment.id] || ''}
                              onChange={(e) => setCommentReplyTexts(prev => ({ ...prev, [comment.id]: e.target.value }))}
                              placeholder={language === 'pt' ? 'Escreva uma resposta de agradecimento ou esclarecimento...' : 'Write an official reply of gratitude or explanation...'}
                              className="flex-1 bg-black border border-zinc-800 p-3 text-xs text-white outline-none focus:border-[#c084fc] transition-all"
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveReply(comment.id, commentReplyTexts[comment.id] || '')}
                              disabled={!(commentReplyTexts[comment.id] || '').trim()}
                              className="px-6 bg-white hover:bg-[#c084fc] text-black hover:text-white font-black text-[10px] uppercase tracking-wider transition-colors disabled:opacity-20 whitespace-nowrap"
                            >
                              {language === 'pt' ? 'Responder' : 'Reply'}
                            </button>
                          </div>
                        </div>
                      )}

              {/* Review delete action */}
                      <div className="flex justify-end pt-4 border-t border-white/5">
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment.id)}
                          className="bg-red-500/10 border border-red-500/20 hover:bg-red-500 hover:text-white text-red-500 font-black text-[9px] uppercase tracking-wider py-2 px-4 transition-all"
                        >
                          {language === 'pt' ? 'Deletar Avaliação Inteira' : 'Delete Entire Review'}
                        </button>
                      </div>

                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        )}

        {activeTab === 'screenshots' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            {/* Header / Upload Form Box */}
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex justify-between items-center mb-10 border-b border-white/5 pb-6">
                <div className="flex items-center gap-4">
                  <ImageIcon className="w-6 h-6 text-[#c084fc]" />
                  <div>
                    <h3 className="text-white font-black uppercase text-sm tracking-widest">
                      {language === 'pt' ? 'Screenshots de Perfis de Usuários' : 'User Profile Screenshots'}
                    </h3>
                    <p className="text-zinc-600 text-[8px] font-black uppercase tracking-[0.4em]">
                      {language === 'pt' ? 'Envie prints reais de perfis Steam decorados para rotacionar na Home' : 'Upload real Steam profile setups to rotate on the homepage'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={refreshScreenshots}
                  disabled={isLoadingScreenshots || isSyncing}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isLoadingScreenshots ? 'animate-spin' : ''}`} />
                  {language === 'pt' ? 'Atualizar' : 'Refresh'}
                </button>
              </div>

              {/* Upload Card */}
              <div className="bg-black/40 border border-white/5 p-6 md:p-8 space-y-6">
                <h4 className="text-white font-black uppercase text-xs tracking-widest flex items-center gap-2">
                  <CloudUpload className="w-4 h-4 text-[#c084fc]" />
                  {language === 'pt' ? 'Adicionar Nova Screenshot de Perfil' : 'Add New Profile Screenshot'}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* File Upload Box */}
                  <div className="space-y-3">
                    <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">
                      {language === 'pt' ? 'Arquivo da Imagem (Print do Perfil Steam)' : 'Image File (Steam Profile Screenshot)'}
                    </label>
                    <div 
                      onClick={() => shotFileInputRef.current?.click()}
                      className="border-2 border-dashed border-zinc-700 hover:border-[#c084fc] bg-zinc-950/60 p-6 flex flex-col items-center justify-center cursor-pointer transition-all rounded min-h-[190px] relative overflow-hidden group"
                    >
                      {shotPreviewUrl ? (
                        <div className="relative w-full h-full flex flex-col items-center">
                          <img 
                            src={shotPreviewUrl} 
                            alt="Preview" 
                            className="max-h-[150px] w-auto object-contain rounded border border-white/10 shadow-lg"
                          />
                          <p className="text-[9px] text-purple-300 font-mono mt-2">Clique para trocar a imagem</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center text-center space-y-2">
                          <Upload className="w-8 h-8 text-zinc-500 group-hover:text-[#c084fc] transition-colors" />
                          <p className="text-xs font-black uppercase text-zinc-300">
                            {language === 'pt' ? 'Clique para selecionar a imagem' : 'Click to select image'}
                          </p>
                          <p className="text-[9px] text-zinc-500 font-mono">PNG, JPG, WEBP até 20MB</p>
                        </div>
                      )}
                      <input 
                        type="file" 
                        ref={shotFileInputRef} 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) {
                            setShotPendingFile(f);
                            setShotPreviewUrl(URL.createObjectURL(f));
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Metadata Fields */}
                  <div className="space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                      <div>
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest block mb-1">
                          {language === 'pt' ? 'Título / Tema do Setup' : 'Title / Setup Theme'}
                        </label>
                        <input 
                          type="text" 
                          placeholder="Ex: Cyberpunk 2077 Steam Profile Showcase" 
                          value={newShotTitle} 
                          onChange={(e) => setNewShotTitle(e.target.value)} 
                          className="w-full bg-black border border-zinc-800 p-3 text-white text-xs outline-none focus:border-[#c084fc]"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest block mb-1">
                            {language === 'pt' ? 'Nome do Usuário/Cliente' : 'Username / Client Name'}
                          </label>
                          <input 
                            type="text" 
                            placeholder="Ex: Alex_V" 
                            value={newShotUserName} 
                            onChange={(e) => setNewShotUserName(e.target.value)} 
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-xs outline-none focus:border-[#c084fc]"
                          />
                        </div>

                        <div>
                          <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest block mb-1">
                            {language === 'pt' ? 'Link Steam (Opcional)' : 'Steam Link (Optional)'}
                          </label>
                          <input 
                            type="text" 
                            placeholder="https://steamcommunity.com/id/..." 
                            value={newShotSteamUrl} 
                            onChange={(e) => setNewShotSteamUrl(e.target.value)} 
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-xs outline-none focus:border-[#c084fc]"
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleUploadScreenshot}
                      disabled={isSyncing || !shotPendingFile}
                      className="w-full py-4 bg-[#c084fc] hover:bg-white text-white hover:text-black font-black uppercase text-[11px] tracking-wider transition-all disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg"
                    >
                      {isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {language === 'pt' ? 'Salvar e Publicar Screenshot' : 'Save and Publish Screenshot'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Existing Screenshots Grid */}
              <div className="mt-12 space-y-6">
                <div className="flex items-center justify-between border-b border-white/5 pb-4">
                  <h4 className="text-white font-black uppercase text-xs tracking-widest flex items-center gap-2">
                    <Layers className="w-4 h-4 text-[#c084fc]" />
                    {language === 'pt' ? 'Screenshots Ativas no Carrossel' : 'Active Carousel Screenshots'} ({screenshotsList.length})
                  </h4>
                  <span className="text-[9px] text-zinc-500 font-mono uppercase">
                    {language === 'pt' ? 'Rotacionam automaticamente na Home' : 'Automatically rotate on Homepage'}
                  </span>
                </div>

                {isLoadingScreenshots ? (
                  <div className="py-16 text-center text-zinc-500 flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-[#c084fc]" />
                    <span className="text-[10px] uppercase font-black tracking-widest">Carregando capturas...</span>
                  </div>
                ) : screenshotsList.length === 0 ? (
                  <div className="py-16 text-center text-zinc-600 bg-black/20 border border-white/5 p-8">
                    <p className="text-xs uppercase font-black tracking-wider mb-2">Nenhuma screenshot cadastrada ainda</p>
                    <p className="text-[10px] text-zinc-600">Use o formulário acima para enviar os primeiros prints de perfis.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {screenshotsList.map((shot) => (
                      <div key={shot.id} className="bg-zinc-950 border border-white/10 overflow-hidden flex flex-col group hover:border-[#c084fc]/50 transition-all">
                        {/* Image Preview */}
                        <div className="relative aspect-video bg-black/80 overflow-hidden">
                          <img 
                            src={shot.imageUrl} 
                            alt={shot.title || 'Screenshot'} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {shot.userName && (
                            <div className="absolute top-2 left-2 bg-black/80 px-2 py-1 rounded text-[9px] font-mono text-purple-300 border border-purple-500/30 flex items-center gap-1">
                              <User className="w-2.5 h-2.5" />
                              <span>{shot.userName}</span>
                            </div>
                          )}
                        </div>

                        {/* Details */}
                        <div className="p-4 flex-1 flex flex-col justify-between space-y-3 bg-zinc-900/30">
                          <div>
                            <h5 className="text-white font-bold text-xs truncate">
                              {shot.title || 'Steam Profile Screenshot'}
                            </h5>
                            {shot.steamUrl && (
                              <a 
                                href={shot.steamUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-[9px] text-zinc-400 hover:text-purple-400 flex items-center gap-1 mt-1 truncate"
                              >
                                <ExternalLink className="w-2.5 h-2.5" />
                                <span className="truncate">{shot.steamUrl}</span>
                              </a>
                            )}
                          </div>

                          <div className="pt-3 border-t border-white/5 flex items-center justify-between">
                            <span className="text-[8px] font-mono text-zinc-600">
                              {shot.id}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteScreenshot(shot.id)}
                              className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500 border border-red-500/20 hover:text-white text-red-500 text-[9px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all"
                            >
                              <Trash2 className="w-3 h-3" />
                              {language === 'pt' ? 'Excluir' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-6">
                <Palette className="w-6 h-6 text-[#c084fc]" />
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Personalização do Santuário</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest block mb-1">Background da Home</label>
                      <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest">Imagem de fundo principal da aplicação</p>
                    </div>
                  </div>

                  <div className="aspect-video bg-black border border-white/5 overflow-hidden relative group">
                    {isVideoUrl(catCoverPreview || categoryCovers['HOME_BG']) ? (
                      <video src={catCoverPreview || categoryCovers['HOME_BG']} autoPlay loop muted className="w-full h-full object-cover opacity-50 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <img src={catCoverPreview || categoryCovers['HOME_BG'] || MASTER_FALLBACK_IMAGE} className="w-full h-full object-cover opacity-50 group-hover:opacity-100 transition-opacity" />
                    )}
                    
                    {editingCategory !== 'HOME_BG' && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all bg-black/40">
                         <button 
                          onClick={() => { setEditingCategory('HOME_BG'); setCatCoverPreview(categoryCovers['HOME_BG']); }}
                          className="bg-white text-black px-8 py-4 text-[10px] font-black uppercase hover:bg-[#c084fc] hover:text-white transition-all shadow-2xl"
                         >
                           Alterar Background
                         </button>
                      </div>
                    )}
                  </div>

                  {editingCategory === 'HOME_BG' && (
                    <div className="space-y-4 animate-in slide-in-from-top-2 duration-300 bg-black/40 p-6 border border-[#c084fc]/20">
                       <div className="flex flex-col gap-2">
                          <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">URL da Imagem/Vídeo</label>
                          <input type="text" placeholder="https://..." value={catCoverPreview || ''} onChange={(e) => setCatCoverPreview(e.target.value)} className="w-full bg-black border border-zinc-800 p-3 text-white text-[10px] outline-none focus:border-[#c084fc]" />
                       </div>
                       <div className="flex gap-2">
                          <button onClick={() => catFileInputRef.current?.click()} className="flex-1 bg-zinc-800 text-white py-3 text-[9px] font-black uppercase hover:bg-zinc-700 transition-all flex items-center justify-center gap-2"><Upload className="w-3 h-3" /> Upload Arquivo</button>
                          <button onClick={handleSaveCategoryCover} disabled={isSyncing} className="flex-1 bg-[#c084fc] text-white py-3 text-[9px] font-black uppercase hover:bg-white hover:text-[#c084fc] transition-all flex items-center justify-center gap-2">{isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Salvar Alteração</button>
                          <button onClick={() => { setEditingCategory(null); setCatPendingFile(null); setCatCoverPreview(null); }} className="px-4 bg-zinc-950 text-zinc-500 py-3 text-[9px] font-black uppercase hover:text-white">Cancelar</button>
                       </div>
                    </div>
                  )}
                  
                  <div className="p-4 bg-black/40 border border-white/5">
                    <p className="text-[8px] text-zinc-500 font-black uppercase leading-relaxed">
                      Dica: Use imagens de alta resolução (1920x1080) para melhor resultado. 
                      O sistema aplica automaticamente um filtro de escurecimento para garantir a legibilidade.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                   <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                      <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <Type className="w-4 h-4 text-[#c084fc]" /> Textos de Introdução (Custom Art)
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Português (PT)</label>
                          <textarea 
                            value={siteSettingsState.customRequestIntro_pt || ''} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, customRequestIntro_pt: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[10px] outline-none focus:border-[#c084fc] resize-none h-20"
                            placeholder="Texto de introdução em Português..."
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Inglês (EN)</label>
                          <textarea 
                            value={siteSettingsState.customRequestIntro_en || ''} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, customRequestIntro_en: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[10px] outline-none focus:border-[#c084fc] resize-none h-20"
                            placeholder="Introduction text in English..."
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Espanhol (ES)</label>
                          <textarea 
                            value={siteSettingsState.customRequestIntro_es || ''} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, customRequestIntro_es: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[10px] outline-none focus:border-[#c084fc] resize-none h-20"
                            placeholder="Texto de introducción en Español..."
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Russo (RU)</label>
                          <textarea 
                            value={siteSettingsState.customRequestIntro_ru || ''} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, customRequestIntro_ru: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[10px] outline-none focus:border-[#c084fc] resize-none h-20"
                            placeholder="Вводный текст на Русском..."
                          />
                        </div>
                        <button 
                          onClick={async () => {
                            setIsSyncing(true);
                            onUpdateSiteSettings(siteSettingsState);
                            const saveRes = await database.saveSettings(siteSettingsState);
                            if (saveRes.success) {
                              alert(language === 'en' ? "Introduction texts updated successfully!" : "Textos de introdução atualizados com sucesso!");
                            } else {
                              alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                            }
                            setIsSyncing(false);
                          }}
                          disabled={isSyncing}
                          className="w-full bg-white hover:bg-[#c084fc] text-black hover:text-white py-3 font-black uppercase text-[10px] flex items-center justify-center gap-2 transition-all cursor-pointer font-bold"
                        >
                          {isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} {language === 'en' ? 'Save Intro Texts' : 'Salvar Textos'}
                        </button>
                      </div>
                   </div>

                   <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                      <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-[#c084fc]" /> Desconto Global e Promoção
                      </h4>
                      <p className="text-[8px] text-zinc-500 font-black uppercase leading-relaxed">
                        Defina uma porcentagem de desconto aplicada a todas as artes e sua respectiva duração (data de expiração). Se ativo, a galeria mostrará o valor normal riscado e o valor promocional abaixo.
                      </p>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Porcentagem do Desconto (%)</label>
                          <input 
                            type="number" 
                            min="0" 
                            max="100" 
                            placeholder="Ex: 15" 
                            value={siteSettingsState.globalDiscountPercentage !== undefined ? siteSettingsState.globalDiscountPercentage : ''} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, globalDiscountPercentage: e.target.value === '' ? undefined : Number(e.target.value) }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#c084fc] font-mono"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block font-sans">Duração do Desconto (Até data/hora)</label>
                          <input 
                            type="datetime-local" 
                            value={siteSettingsState.globalDiscountUntil || ''} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, globalDiscountUntil: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#c084fc] font-mono"
                          />
                        </div>
                        <button 
                          onClick={async () => {
                            setIsSyncing(true);
                            const saveRes = await database.saveSettings(siteSettingsState);
                            if (saveRes.success) {
                              onUpdateSiteSettings(siteSettingsState);
                              alert(language === 'en' ? "Discount configurations updated!" : "Configurações de desconto atualizadas!");
                            } else {
                              alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                            }
                            setIsSyncing(false);
                          }}
                          disabled={isSyncing}
                          className="w-full bg-white hover:bg-[#c084fc] text-black hover:text-white py-3 font-black uppercase text-[10px] flex items-center justify-center gap-2"
                        >
                          {isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Salvar Desconto Global
                        </button>
                      </div>
                   </div>

                   <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                      <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-sky-400" /> Configuração do Stripe Payment
                      </h4>
                      <p className="text-[8px] text-zinc-500 font-black uppercase leading-relaxed">
                        Insira a sua chave secreta do Stripe (<code className="text-sky-400">sk_test_...</code> ou <code className="text-sky-400">sk_live_...</code>). Você também pode configurá-la através da variável de ambiente <code className="text-sky-400">STRIPE_SECRET_KEY</code> em seu servidor.
                      </p>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block font-sans">Stripe Secret Key (sk_test_... ou sk_live_...)</label>
                          <input 
                            type="password" 
                            placeholder="sk_test_51..." 
                            value={siteSettingsState.stripeSecretKey || ''} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, stripeSecretKey: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-sky-400 font-mono"
                          />
                        </div>
                        <button 
                          onClick={async () => {
                            setIsSyncing(true);
                            const saveRes = await database.saveSettings(siteSettingsState);
                            if (saveRes.success) {
                              onUpdateSiteSettings(siteSettingsState);
                              alert(language === 'en' ? "Stripe settings updated!" : "Configurações do Stripe salvas com sucesso!");
                            } else {
                              alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                            }
                            setIsSyncing(false);
                          }}
                          disabled={isSyncing}
                          className="w-full bg-sky-500 hover:bg-sky-400 text-black py-3 font-black uppercase text-[10px] flex items-center justify-center gap-2 transition-colors"
                        >
                          {isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Salvar Chave do Stripe
                        </button>
                      </div>
                   </div>

                   <div className="p-8 bg-zinc-950 border border-white/5 space-y-4">
                      <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-green-500" /> Status do Sistema
                      </h4>
                      <div className="space-y-2">
                         <div className="flex justify-between text-[9px] font-black uppercase">
                            <span className="text-zinc-500">Versão dos Dados</span>
                            <span className="text-white">v2.0.4</span>
                         </div>
                         <div className="flex justify-between text-[9px] font-black uppercase">
                            <span className="text-zinc-500">Servidor</span>
                            <span className="text-green-500">Online (Hostinger)</span>
                         </div>
                         <div className="flex justify-between text-[9px] font-black uppercase">
                            <span className="text-zinc-500">Cache Local</span>
                            <span className="text-[#c084fc]">Ativo</span>
                         </div>
                      </div>
                   </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'footer' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-6">
                <LinkIcon className="w-6 h-6 text-[#c084fc]" />
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Links do Rodapé</h3>
              </div>

              <div className="max-w-2xl space-y-8">
                <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                  <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                    <Settings className="w-4 h-4 text-[#c084fc]" /> Configurar Redes Sociais no Rodapé
                  </h4>
                  
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest block font-sans">Disponibilidade dos Botões</label>
                      <p className="text-[8px] text-zinc-650 font-black uppercase leading-relaxed font-sans">
                        Os links configurados abaixo aparecerão como botões clicáveis no rodapé da página inicial (Home), logo abaixo da seção de avaliações dos clientes (Client Reviews).
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#ff5a5f] rounded-full animate-pulse" /> DeviantArt (URL)
                      </label>
                      <input 
                        type="text" 
                        placeholder="https://www.deviantart.com/seu-perfil" 
                        value={siteSettingsState.deviantartUrl || ''} 
                        onChange={(e) => setSiteSettingsState(prev => ({ ...prev, deviantartUrl: e.target.value }))}
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#c084fc] font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#229ED9] rounded-full animate-pulse" /> Telegram (URL)
                      </label>
                      <input 
                        type="text" 
                        placeholder="https://t.me/seu-usuario ou canal" 
                        value={siteSettingsState.telegramUrl || ''} 
                        onChange={(e) => setSiteSettingsState(prev => ({ ...prev, telegramUrl: e.target.value }))}
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#c084fc] font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#5865F2] rounded-full animate-pulse" /> Discord (URL)
                      </label>
                      <input 
                        type="text" 
                        placeholder="https://discord.gg/seu-convite" 
                        value={siteSettingsState.discordUrl || ''} 
                        onChange={(e) => setSiteSettingsState(prev => ({ ...prev, discordUrl: e.target.value }))}
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#c084fc] font-mono"
                      />
                    </div>

                    <button 
                      onClick={async () => {
                        setIsSyncing(true);
                        const saveRes = await database.saveSettings(siteSettingsState);
                        if (saveRes.success) {
                          onUpdateSiteSettings(siteSettingsState);
                          alert("Links do rodapé atualizados com sucesso!");
                        } else {
                          alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                        }
                        setIsSyncing(false);
                      }}
                      disabled={isSyncing}
                      className="w-full bg-white hover:bg-[#c084fc] text-black hover:text-white py-4 font-black uppercase text-[10px] flex items-center justify-center gap-2 tracking-widest transition-all mt-4 border-b-4 border-zinc-300 hover:border-[#c084fc] active:scale-95"
                    >
                      {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salvar Configurações de Rodapé
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'faq' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-6">
                <HelpCircle className="w-6 h-6 text-[#c084fc]" />
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Gerenciar F.A.Q. (PT / EN / ES / RU)</h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Add New FAQ Form */}
                <div className="space-y-6">
                  <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                    <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                      <Plus className="w-4 h-4 text-[#c084fc]" /> Adicionar Nova Pergunta
                    </h4>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[8px] font-black text-zinc-400 uppercase tracking-widest block">Pergunta (Português - PT)</label>
                          <input 
                            type="text"
                            id="new-faq-q-pt"
                            placeholder="Ex: Como funciona o prazo de entrega?"
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[11px] outline-none focus:border-[#c084fc] font-sans"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] font-black text-zinc-400 uppercase tracking-widest block">Pergunta (Inglês - EN)</label>
                          <input 
                            type="text"
                            id="new-faq-q-en"
                            placeholder="Ex: How long does delivery take?"
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[11px] outline-none focus:border-[#c084fc] font-sans"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[8px] font-black text-zinc-400 uppercase tracking-widest block">Pergunta (Espanhol - ES)</label>
                          <input 
                            type="text"
                            id="new-faq-q-es"
                            placeholder="Ex: ¿Cuánto tarda la entrega?"
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[11px] outline-none focus:border-[#c084fc] font-sans"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] font-black text-zinc-400 uppercase tracking-widest block">Pergunta (Russo - RU)</label>
                          <input 
                            type="text"
                            id="new-faq-q-ru"
                            placeholder="Ex: Сколько времени занимает доставка?"
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[11px] outline-none focus:border-[#c084fc] font-sans"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[8px] font-black text-zinc-400 uppercase tracking-widest block">Resposta (Português - PT)</label>
                          <textarea 
                            id="new-faq-a-pt"
                            rows={2}
                            placeholder="Ex: Nossos prazos variam de 3 a 5 dias..."
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[11px] outline-none focus:border-[#c084fc] font-sans resize-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] font-black text-zinc-400 uppercase tracking-widest block">Resposta (Inglês - EN)</label>
                          <textarea 
                            id="new-faq-a-en"
                            rows={2}
                            placeholder="Ex: Delivery takes 3 to 5 days..."
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[11px] outline-none focus:border-[#c084fc] font-sans resize-none"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[8px] font-black text-zinc-400 uppercase tracking-widest block">Resposta (Espanhol - ES)</label>
                          <textarea 
                            id="new-faq-a-es"
                            rows={2}
                            placeholder="Ex: Nuestros plazos varían de 3 a 5 días..."
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[11px] outline-none focus:border-[#c084fc] font-sans resize-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] font-black text-zinc-400 uppercase tracking-widest block">Resposta (Russo - RU)</label>
                          <textarea 
                            id="new-faq-a-ru"
                            rows={2}
                            placeholder="Ex: Сроки составляют от 3 до 5 дней..."
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[11px] outline-none focus:border-[#c084fc] font-sans resize-none"
                          />
                        </div>
                      </div>

                      <button 
                        type="button"
                        onClick={() => {
                          const qPtEl = document.getElementById('new-faq-q-pt') as HTMLInputElement;
                          const qEnEl = document.getElementById('new-faq-q-en') as HTMLInputElement;
                          const qEsEl = document.getElementById('new-faq-q-es') as HTMLInputElement;
                          const qRuEl = document.getElementById('new-faq-q-ru') as HTMLInputElement;
                          const aPtEl = document.getElementById('new-faq-a-pt') as HTMLTextAreaElement;
                          const aEnEl = document.getElementById('new-faq-a-en') as HTMLTextAreaElement;
                          const aEsEl = document.getElementById('new-faq-a-es') as HTMLTextAreaElement;
                          const aRuEl = document.getElementById('new-faq-a-ru') as HTMLTextAreaElement;

                          if (!qPtEl?.value && !qEnEl?.value) {
                            alert("Por favor, preencha pelo menos a pergunta em Português ou Inglês!");
                            return;
                          }

                          const newFaq = {
                            id: 'faq_' + Date.now(),
                            question_pt: qPtEl?.value || qEnEl?.value || '',
                            question_en: qEnEl?.value || qPtEl?.value || '',
                            question_es: qEsEl?.value || '',
                            question_ru: qRuEl?.value || '',
                            answer_pt: aPtEl?.value || aEnEl?.value || '',
                            answer_en: aEnEl?.value || aPtEl?.value || '',
                            answer_es: aEsEl?.value || '',
                            answer_ru: aRuEl?.value || ''
                          };

                          const currentFaqs = siteSettingsState.faqs || [];
                          const updatedFaqs = [...currentFaqs, newFaq];

                          setSiteSettingsState(prev => ({
                            ...prev,
                            faqs: updatedFaqs
                          }));

                          // Reset inputs
                          if (qPtEl) qPtEl.value = '';
                          if (qEnEl) qEnEl.value = '';
                          if (qEsEl) qEsEl.value = '';
                          if (qRuEl) qRuEl.value = '';
                          if (aPtEl) aPtEl.value = '';
                          if (aEnEl) aEnEl.value = '';
                          if (aEsEl) aEsEl.value = '';
                          if (aRuEl) aRuEl.value = '';
                          
                          alert("Pergunta adicionada à lista temporária. Lembre-se de clicar em salvar no final!");
                        }}
                        className="w-full bg-white hover:bg-[#c084fc] text-black hover:text-white py-4 font-black uppercase text-[10px] tracking-widest transition-all border-b-2 border-zinc-300 hover:border-[#c084fc] active:scale-95"
                      >
                        Incluir Pergunta na Lista
                      </button>
                    </div>
                  </div>
                </div>

                {/* FAQ List and Save */}
                <div className="space-y-6">
                  <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <HelpCircle className="w-4 h-4 text-[#c084fc]" /> Perguntas Existentes ({siteSettingsState.faqs?.length || 0})
                      </h4>
                      {(!siteSettingsState.faqs || siteSettingsState.faqs.length === 0) && (
                        <button
                          type="button"
                          onClick={() => {
                            const defaultFaqs = [
                              {
                                id: 'faq_1',
                                question_pt: 'Como funcionam os pedidos de arte personalizada?',
                                question_en: 'How do custom art orders work?',
                                question_es: '¿Cómo funcionan los pedidos de arte personalizado?',
                                question_ru: 'Как работают индивидуальные заказы на арт?',
                                answer_pt: 'Para fazer um pedido, basta criar uma conta ou fazer o login, navegar pelas abas do site e selecionar a categoria apropriada. Você pode escolher dimensões, enviar referências de fundo, customizar textos e adicionar detalhes adicionais. O progresso pode ser acompanhado diretamente pelo seu painel de usuário.',
                                answer_en: 'To place an order, simply create an account or log in, browse the categories, and request custom designs. You can specify dimensions, configure background images, add custom texts, and more. All steps of your order can be tracked in real-time in your client dashboard.',
                                answer_es: 'Para realizar un pedido, simplemente crea una cuenta o inicia sesión, explora las categorías del sitio y selecciona la adecuada. Puedes elegir dimensiones, enviar referencias de fondo, personalizar textos y añadir detalles adicionales. El progreso se puede seguir directamente desde tu panel de usuario.',
                                answer_ru: 'Чтобы сделать заказ, просто создайте аккаунт или войдите в систему, перейдите в нужную категорию и выберите оформление. Вы можете выбрать размеры, прикрепить фон, настроить текст и указать дополнительные детали. Отслеживать прогресс можно в реальном времени в личном кабинете.'
                              },
                              {
                                id: 'faq_2',
                                question_pt: 'Quais são os formatos de entrega?',
                                question_en: 'What are the delivery formats?',
                                question_es: '¿Cuáles son los formatos de entrega?',
                                question_ru: 'В каких форматах доставляются готовые работы?',
                                answer_pt: 'Nossas artes para a Steam são otimizadas para atender perfeitamente aos padrões da plataforma. Entregamos arquivos GIF de alta qualidade para artes animadas e PNGs em alta definição para artes estáticas, além de links e instruções para o upload perfeito sem perdas de qualidade.',
                                answer_en: 'Our Steam artworks are optimized to fully comply with the platform standards. We deliver high-quality GIF files for animated backgrounds and high-definition PNGs for static artworks, alongside instructions for an absolute lossless upload.',
                                answer_es: 'Nuestras obras para Steam están optimizadas para cumplir perfectamente con los estándares de la plataforma. Entregamos archivos GIF de alta calidad para artes animadas y PNGs en alta definición para artes estáticas, además de enlaces e instrucciones para subirlas sin pérdida de calidad.',
                                answer_ru: 'Наши арты для Steam оптимизированы в строгом соответствии со стандартами платформы. Мы предоставляем высококачественные GIF-файлы для анимаций и PNG высокого разрешения для статичных артов, а также инструкции по загрузке без потери качества.'
                              },
                              {
                                id: 'faq_3',
                                question_pt: 'Quais métodos de pagamento são aceitos?',
                                question_en: 'What payment methods are accepted?',
                                question_es: '¿Qué métodos de pago se aceptan?',
                                question_ru: 'Какие способы оплаты принимаются?',
                                answer_pt: 'Oferecemos opções modernas para transações rápidas e integradas de acordo com as especificações do Santuário. Todos os detalhes de orçamento e acréscimos adicionais para customizações extras podem ser sanados através do nosso canal direto no chat ou contatos de suporte.',
                                answer_en: 'We support modern payment methods to initiate projects instantly. Detailed cost estimates and additional customization fees can be comfortably negotiated or discussed in detail through our real-time messenger chat or Support.',
                                answer_es: 'Ofrecemos métodos de pago modernos para transacciones rápidas e integradas según las especificaciones del Santuario. Todos los detalles de presupuesto y tarifas adicionales para personalizaciones extras pueden consultarse a través de nuestro chat directo o canales de soporte.',
                                answer_ru: 'Мы поддерживаем современные и безопасные способы оплаты. Все детали сметы и стоимость дополнительных улучшений можно обсудить напрямую через наш встроенный чат или со службой поддержки.'
                              }
                            ];
                            setSiteSettingsState(prev => ({ ...prev, faqs: defaultFaqs }));
                          }}
                          className="text-[8px] bg-zinc-900 border border-zinc-800 text-[#c084fc] hover:bg-[#c084fc]/10 px-3 py-1.5 font-black uppercase tracking-widest transition-all"
                        >
                          Carregar Padrão
                        </button>
                      )}
                    </div>

                    <div className="space-y-4 max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
                      {(!siteSettingsState.faqs || siteSettingsState.faqs.length === 0) ? (
                        <div className="text-center py-10 border border-dashed border-zinc-900">
                          <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wide">Nenhuma pergunta na lista.</p>
                          <p className="text-[8px] text-zinc-650 font-medium uppercase mt-1">Carregue o padrão ou adicione perguntas ao lado.</p>
                        </div>
                      ) : (
                        siteSettingsState.faqs.map((faq, idx) => (
                          <div key={faq.id} className="p-4 bg-black border border-zinc-900 space-y-4 relative group">
                            <div className="flex items-start justify-between gap-4 border-b border-zinc-900 pb-3">
                              <span className="text-[9px] font-mono font-bold text-[#c084fc] bg-[#c084fc]/10 px-2 py-0.5">#{idx + 1}</span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (idx === 0) return;
                                    const items = [...(siteSettingsState.faqs || [])];
                                    const temp = items[idx];
                                    items[idx] = items[idx - 1];
                                    items[idx - 1] = temp;
                                    setSiteSettingsState(prev => ({ ...prev, faqs: items }));
                                  }}
                                  disabled={idx === 0}
                                  className="p-1 bg-zinc-950 border border-zinc-900 text-zinc-500 hover:text-white disabled:opacity-20 transition-all text-[8px] font-mono"
                                  title="Subir"
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (idx === (siteSettingsState.faqs?.length || 0) - 1) return;
                                    const items = [...(siteSettingsState.faqs || [])];
                                    const temp = items[idx];
                                    items[idx] = items[idx + 1];
                                    items[idx + 1] = temp;
                                    setSiteSettingsState(prev => ({ ...prev, faqs: items }));
                                  }}
                                  disabled={idx === (siteSettingsState.faqs?.length || 0) - 1}
                                  className="p-1 bg-zinc-950 border border-zinc-900 text-zinc-500 hover:text-white disabled:opacity-20 transition-all text-[8px] font-mono"
                                  title="Descer"
                                >
                                  ▼
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => {
                                    if(confirm("Deseja realmente remover esta pergunta?")) {
                                      const updated = siteSettingsState.faqs?.filter(f => f.id !== faq.id) || [];
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }
                                  }}
                                  className="p-1 bg-zinc-950 border border-zinc-900 text-zinc-500 hover:text-red-500 transition-all"
                                  title="Remover Pergunta"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-[7px] font-black text-zinc-500 uppercase tracking-widest block">Pergunta PT</label>
                                  <input 
                                    type="text"
                                    value={faq.question_pt || ''}
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].question_pt = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-1.5 text-white text-[10px] outline-none focus:border-[#c084fc] font-sans"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[7px] font-black text-zinc-500 uppercase tracking-widest block">Pergunta EN</label>
                                  <input 
                                    type="text"
                                    value={faq.question_en || ''}
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].question_en = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-1.5 text-white text-[10px] outline-none focus:border-[#c084fc] font-sans"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-[7px] font-black text-zinc-500 uppercase tracking-widest block">Pergunta ES</label>
                                  <input 
                                    type="text"
                                    value={faq.question_es || ''}
                                    placeholder="En español..."
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].question_es = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-1.5 text-white text-[10px] outline-none focus:border-[#c084fc] font-sans"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[7px] font-black text-zinc-500 uppercase tracking-widest block">Pergunta RU</label>
                                  <input 
                                    type="text"
                                    value={faq.question_ru || ''}
                                    placeholder="На русском..."
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].question_ru = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-1.5 text-white text-[10px] outline-none focus:border-[#c084fc] font-sans"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-[7px] font-black text-zinc-500 uppercase tracking-widest block">Resposta PT</label>
                                  <textarea 
                                    rows={2}
                                    value={faq.answer_pt || ''}
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].answer_pt = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-1.5 text-white text-[10px] outline-none focus:border-[#c084fc] font-sans resize-none"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[7px] font-black text-zinc-500 uppercase tracking-widest block">Resposta EN</label>
                                  <textarea 
                                    rows={2}
                                    value={faq.answer_en || ''}
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].answer_en = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-1.5 text-white text-[10px] outline-none focus:border-[#c084fc] font-sans resize-none"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-[7px] font-black text-zinc-500 uppercase tracking-widest block">Resposta ES</label>
                                  <textarea 
                                    rows={2}
                                    value={faq.answer_es || ''}
                                    placeholder="Respuesta en español..."
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].answer_es = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-1.5 text-white text-[10px] outline-none focus:border-[#c084fc] font-sans resize-none"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[7px] font-black text-zinc-500 uppercase tracking-widest block">Resposta RU</label>
                                  <textarea 
                                    rows={2}
                                    value={faq.answer_ru || ''}
                                    placeholder="Ответ на русском..."
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].answer_ru = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-1.5 text-white text-[10px] outline-none focus:border-[#c084fc] font-sans resize-none"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <button 
                      type="button"
                      onClick={async () => {
                        setIsSyncing(true);
                        const saveRes = await database.saveSettings(siteSettingsState);
                        if (saveRes.success) {
                          onUpdateSiteSettings(siteSettingsState);
                          alert("Perguntas do F.A.Q. salvas com sucesso!");
                        } else {
                          alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                        }
                        setIsSyncing(false);
                      }}
                      disabled={isSyncing}
                      className="w-full bg-[#c084fc] hover:bg-[#c084fc]/90 text-white py-4 font-black uppercase text-[10px] flex items-center justify-center gap-2 tracking-widest transition-all mt-4 border-b-4 border-rose-400 hover:border-[#c084fc] active:scale-95"
                    >
                      {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salvar Banco de F.A.Q
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                {!isAddingArt ? (
                  <button onClick={() => setIsAddingArt(true)} className="bg-[#c084fc] text-white px-8 py-4 font-black uppercase text-xs flex items-center justify-center gap-3 hover:scale-105 transition-all">
                    <Plus className="w-4 h-4" /> Adicionar Nova Obra
                  </button>
                ) : (
                  <div></div>
                )}
                <button
                  onClick={handleOptimizeMissingCovers}
                  disabled={isOptimizingCovers}
                  className="bg-purple-950/60 hover:bg-purple-900 border border-purple-500/50 text-purple-200 hover:text-white px-6 py-4 font-black uppercase text-xs flex items-center justify-center gap-2.5 transition-all shadow-[0_0_20px_rgba(168,85,247,0.2)] disabled:opacity-50"
                  title="Gera imagens de capa JPG para todas as obras de vídeo que estão sem capa, permitindo indexação no Google Imagens"
                >
                  {isOptimizingCovers ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-[#c084fc]" />
                      <span>{optimizeProgress ? `Otimizando (${optimizeProgress.current}/${optimizeProgress.total})...` : 'Processando...'}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 text-[#c084fc]" />
                      <span>{language === 'pt' ? 'Otimizar Capas p/ Google Imagens' : 'Optimize Covers for Google Images'}</span>
                    </>
                  )}
                </button>
              </div>

              {/* Admin Gallery Search & Violet Transparent Filters */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {/* Search Bar by Name */}
                <div className="relative max-w-xs sm:w-64 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-purple-400/70 group-hover:text-purple-300 transition-colors" />
                  <input 
                    type="text" 
                    value={gallerySearch} 
                    onChange={(e) => setGallerySearch(e.target.value)} 
                    placeholder={language === 'en' ? "Search artwork..." : "Buscar arte..."} 
                    className="w-full bg-transparent border border-purple-900/40 hover:border-purple-500/50 focus:border-purple-500 py-2 pl-9 pr-8 text-xs text-purple-200 placeholder-purple-400/40 outline-none focus:text-white transition-all font-medium rounded-none shadow-[0_0_10px_rgba(168,85,247,0.1)] focus:shadow-[0_0_15px_rgba(168,85,247,0.25)]" 
                  />
                  {gallerySearch && (
                    <button 
                      onClick={() => setGallerySearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-purple-400/70 hover:text-purple-200 p-1"
                      title={language === 'en' ? 'Clear search' : 'Limpar busca'}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Transparent Violet Filters for Price & Recent */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Recent / Recentes Filter */}
                  <div className="flex items-center">
                    <button
                      onClick={() => {
                        if (gallerySortBy !== 'date') {
                          setGallerySortBy('date');
                          setGallerySortOrder('desc');
                        } else {
                          setGallerySortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                        }
                      }}
                      className={`px-3.5 py-2 text-[11px] font-black uppercase tracking-wider border transition-all flex items-center gap-2 ${
                        gallerySortBy === 'date'
                          ? 'bg-purple-950/30 text-purple-300 border-purple-500/80 shadow-[0_0_15px_rgba(168,85,247,0.35)]'
                          : 'bg-transparent text-purple-300/60 border-purple-900/30 hover:border-purple-500/50 hover:text-purple-200 hover:bg-purple-950/10'
                      }`}
                    >
                      <Calendar className="w-3.5 h-3.5 text-purple-400" />
                      <span>{language === 'en' ? 'Recent' : 'Recentes'}</span>
                      {gallerySortBy === 'date' && (
                        gallerySortOrder === 'desc' 
                          ? <ArrowDown className="w-3 h-3 text-purple-400" /> 
                          : <ArrowUp className="w-3 h-3 text-purple-400" />
                      )}
                    </button>
                  </div>

                  {/* Price Filter */}
                  <div className="flex items-center">
                    <button
                      onClick={() => {
                        if (gallerySortBy !== 'price') {
                          setGallerySortBy('price');
                          setGallerySortOrder('asc');
                        } else {
                          setGallerySortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                        }
                      }}
                      className={`px-3.5 py-2 text-[11px] font-black uppercase tracking-wider border transition-all flex items-center gap-2 ${
                        gallerySortBy === 'price'
                          ? 'bg-purple-950/30 text-purple-300 border-purple-500/80 shadow-[0_0_15px_rgba(168,85,247,0.35)]'
                          : 'bg-transparent text-purple-300/60 border-purple-900/30 hover:border-purple-500/50 hover:text-purple-200 hover:bg-purple-950/10'
                      }`}
                    >
                      <DollarSign className="w-3.5 h-3.5 text-purple-400" />
                      <span>{language === 'en' ? 'Price' : 'Preço'}</span>
                      {gallerySortBy === 'price' && (
                        gallerySortOrder === 'asc' 
                          ? <span className="text-[9px] font-mono text-purple-400 font-bold">({language === 'en' ? 'Low to High' : 'Menor'})</span> 
                          : <span className="text-[9px] font-mono text-purple-400 font-bold">({language === 'en' ? 'High to Low' : 'Maior'})</span>
                      )}
                    </button>
                  </div>

                  {/* Reset Sorting Button (if active) */}
                  {gallerySortBy !== 'default' && (
                    <button
                      onClick={() => {
                        setGallerySortBy('default');
                        setGallerySortOrder('desc');
                      }}
                      className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-purple-400/60 hover:text-purple-200 bg-transparent border border-purple-900/30 hover:border-purple-500/40 hover:bg-purple-950/20 transition-all flex items-center gap-1.5"
                      title={language === 'en' ? 'Reset sorting' : 'Limpar ordenação'}
                    >
                      <RefreshCw className="w-3 h-3 text-purple-400" />
                      <span>{language === 'en' ? 'Reset' : 'Padrão'}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {isAddingArt && (
              <div ref={editFormRef} className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 animate-in slide-in-from-top-4 duration-500">
                <div className="flex justify-between items-center mb-10 border-b border-white/5 pb-6">
                   <h3 className="text-white font-black uppercase text-sm">{editingArt ? 'Editando Obra' : 'Nova Obra'}</h3>
                   <button onClick={resetForm} className="text-zinc-500 hover:text-white transition-colors"><X className="w-6 h-6" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[400px_1fr] gap-16">
                  <div className="space-y-6">
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-[9/14] bg-black border-2 border-dashed border-zinc-800 flex flex-col items-center justify-center cursor-pointer hover:border-[#c084fc] transition-all overflow-hidden relative group"
                    >
                      {previewUrl ? (
                        <>
                          {isVideoUrl(previewUrl) ? (
                            <video src={previewUrl} autoPlay loop muted className="w-full h-full object-cover" />
                          ) : (
                            <img src={previewUrl} className="w-full h-full object-cover" />
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                            <RefreshCw className="w-10 h-10 text-white" />
                          </div>
                        </>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-zinc-700 mb-4" />
                          <p className="text-[10px] font-black uppercase text-zinc-600 tracking-widest text-center px-4">Arraste ou clique para selecionar Mídia</p>
                        </>
                      )}
                    </div>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} accept="image/*,video/mp4" />
                    
                    <div className="flex flex-col gap-4">
                      <div className="p-4 bg-black border border-zinc-800 space-y-3">
                        <span className="text-[9px] font-black text-zinc-500 uppercase flex items-center gap-2">
                          <LayoutGrid className="w-3.5 h-3.5 text-[#c084fc]" /> Proporção da Galeria
                        </span>
                        <div className="grid grid-cols-3 gap-2">
                          {(['vertical', 'square', 'horizontal'] as const).map((aspect) => (
                            <button
                              key={aspect}
                              type="button"
                              onClick={() => setNewArt({ ...newArt, aspectRatio: aspect })}
                              className={`py-2 text-[8px] font-black uppercase border tracking-wider transition-all text-center ${
                                (newArt.aspectRatio === aspect || (!newArt.aspectRatio && aspect === 'vertical'))
                                  ? 'border-[#c084fc] bg-[#c084fc]/10 text-[#c084fc]'
                                  : 'border-zinc-800 hover:border-zinc-500 text-zinc-500 hover:text-white'
                              }`}
                            >
                              {aspect === 'vertical' && 'V (9:14)'}
                              {aspect === 'square' && 'Q (1:1)'}
                              {aspect === 'horizontal' && 'H (16:9)'}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-black border border-zinc-800">
                        <span className="text-[9px] font-black text-zinc-500 uppercase flex items-center gap-2">
                          <FileVideo className="w-3.5 h-3.5" /> Animado?
                        </span>
                        <button onClick={() => setNewArt({...newArt, isAnimated: !newArt.isAnimated})} className="text-[#c084fc]">
                          {newArt.isAnimated ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-30" />}
                        </button>
                      </div>

                      <div className={`flex items-center justify-between p-4 bg-black border transition-all ${newArt.notForSale ? 'border-red-500/50' : 'border-zinc-800'}`}>
                        <span className={`text-[9px] font-black uppercase flex items-center gap-2 ${newArt.notForSale ? 'text-red-500' : 'text-zinc-500'}`}>
                          <Ban className="w-3.5 h-3.5" /> Not for Sale
                        </span>
                        <button onClick={() => setNewArt({...newArt, notForSale: !newArt.notForSale})} className={newArt.notForSale ? 'text-red-500' : 'text-zinc-500'}>
                          {newArt.notForSale ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-30" />}
                        </button>
                      </div>

                      <div className={`flex items-center justify-between p-4 bg-black border transition-all ${newArt.hideSignature ? 'border-[#c084fc]/50' : 'border-zinc-800'}`}>
                        <span className={`text-[9px] font-black uppercase flex items-center gap-2 ${newArt.hideSignature ? 'text-[#c084fc]' : 'text-zinc-500'}`}>
                          <Type className="w-3.5 h-3.5" /> Ocultar Assinatura / Fonte
                        </span>
                        <button onClick={() => setNewArt({...newArt, hideSignature: !newArt.hideSignature})} className={newArt.hideSignature ? 'text-[#c084fc]' : 'text-zinc-500'}>
                          {newArt.hideSignature ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-30" />}
                        </button>
                      </div>

                      <div className={`flex items-center justify-between p-4 bg-black border transition-all ${newArt.hideDiscord ? 'border-[#c084fc]/50' : 'border-zinc-800'}`}>
                        <span className={`text-[9px] font-black uppercase flex items-center gap-2 ${newArt.hideDiscord ? 'text-[#c084fc]' : 'text-zinc-500'}`}>
                          <MessageSquare className="w-3.5 h-3.5" /> Ocultar Discord
                        </span>
                        <button onClick={() => setNewArt({...newArt, hideDiscord: !newArt.hideDiscord})} className={newArt.hideDiscord ? 'text-[#c084fc]' : 'text-zinc-500'}>
                          {newArt.hideDiscord ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-30" />}
                        </button>
                      </div>

                      <div className={`flex items-center justify-between p-4 bg-black border transition-all ${newArt.hideNotes ? 'border-[#c084fc]/50' : 'border-zinc-800'}`}>
                        <span className={`text-[9px] font-black uppercase flex items-center gap-2 ${newArt.hideNotes ? 'text-[#c084fc]' : 'text-zinc-500'}`}>
                          <AlertCircle className="w-3.5 h-3.5" /> Ocultar Observações
                        </span>
                        <button onClick={() => setNewArt({...newArt, hideNotes: !newArt.hideNotes})} className={newArt.hideNotes ? 'text-[#c084fc]' : 'text-zinc-500'}>
                          {newArt.hideNotes ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-30" />}
                        </button>
                      </div>

                      <div className={`flex items-center justify-between p-4 bg-black border transition-all ${newArt.hideBuyButton ? 'border-[#c084fc]/50' : 'border-zinc-800'}`}>
                        <span className={`text-[9px] font-black uppercase flex items-center gap-2 ${newArt.hideBuyButton ? 'text-[#c084fc]' : 'text-zinc-500'}`}>
                          <Heart className="w-3.5 h-3.5" /> Ocultar "Adquirir Arte"
                        </span>
                        <button onClick={() => setNewArt({...newArt, hideBuyButton: !newArt.hideBuyButton})} className={newArt.hideBuyButton ? 'text-[#c084fc]' : 'text-zinc-500'}>
                          {newArt.hideBuyButton ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-30" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Título da Obra (PT)</label>
                        <input type="text" value={newArt.title?.pt} onChange={e => setNewArt({...newArt, title: {...newArt.title, pt: e.target.value}})} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc]" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Título da Obra (EN)</label>
                        <input type="text" value={newArt.title?.en} onChange={e => setNewArt({...newArt, title: {...newArt.title, en: e.target.value}})} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc]" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Categoria</label>
                        <select value={newArt.category} onChange={e => {
                          const cat = e.target.value as ArtCategory;
                          const defaultSub = ART_SUBCATEGORIES[cat]?.[0]?.id || '';
                          setNewArt({
                            ...newArt,
                            category: cat,
                            subcategory: defaultSub
                          });
                        }} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc]">
                          {Object.values(ArtCategory).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Investimento ($)</label>
                        <div className="relative">
                          <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
                          <input type="number" value={newArt.price} onChange={e => setNewArt({...newArt, price: parseFloat(e.target.value)})} className="w-full bg-black border border-zinc-800 p-4 pl-10 text-white text-xs outline-none focus:border-[#c084fc]" />
                        </div>
                      </div>
                    </div>

                    {newArt.category && ART_SUBCATEGORIES[newArt.category] && (
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">
                          {language === 'en' ? `Subcategory (${newArt.category})` : `Subcategoria (${newArt.category})`}
                        </label>
                        <select 
                          value={newArt.subcategory || (ART_SUBCATEGORIES[newArt.category]?.[0]?.id || '')} 
                          onChange={e => setNewArt({...newArt, subcategory: e.target.value})} 
                          className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc]"
                        >
                          {ART_SUBCATEGORIES[newArt.category].map(sub => (
                            <option key={sub.id} value={sub.id}>
                              {sub.label[language] || sub.id}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">
                        {language === 'en' ? 'Art Type' : 'Tipo de Arte'}
                      </label>
                      <select 
                        value={newArt.artType || 'Single Artwork'} 
                        onChange={e => setNewArt({...newArt, artType: e.target.value})} 
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc]"
                      >
                        {ART_TYPES.map(type => (
                          <option key={type.id} value={type.id}>
                            {language === 'en' ? type.label.en : type.label.pt}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                        <LinkIcon className="w-3.5 h-3.5 text-[#c084fc]" /> Link do Mercado Steam (Papel de Parede)
                      </label>
                      <input 
                        type="text" 
                        value={newArt.steamBackgroundUrl} 
                        onChange={e => setNewArt({...newArt, steamBackgroundUrl: e.target.value})} 
                        placeholder="https://steamcommunity.com/market/listings/753/..."
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc] font-mono" 
                      />
                    </div>

                    <div className="space-y-4 bg-[#c084fc]/5 border border-[#c084fc]/30 p-5">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-black text-[#c084fc] uppercase tracking-widest flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-[#c084fc]" />
                          {language === 'pt' ? 'Imagem de Capa / Thumbnail (Google Imagens)' : 'Cover Image / Thumbnail (Google Images)'}
                        </label>
                        <span className="text-[8px] font-mono bg-[#c084fc]/20 text-[#c084fc] px-2 py-0.5 rounded font-black uppercase">
                          Indexação no Google SEO
                        </span>
                      </div>
                      <p className="text-[10px] text-zinc-400 leading-relaxed">
                        {language === 'pt' 
                          ? 'Arraste uma imagem abaixo ou selecione um arquivo estático (JPG, PNG, GIF, WEBP) para o Google Imagens indexar e para servir de capa da arte.' 
                          : 'Drag an image below or select a static file (JPG, PNG, GIF, WEBP) for Google Images indexing and artwork cover poster.'}
                      </p>

                      {/* Dropzone for Cover Upload */}
                      <div 
                        onDragOver={(e) => { e.preventDefault(); setIsDragOverCover(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setIsDragOverCover(false); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDragOverCover(false);
                          const file = e.dataTransfer.files?.[0];
                          if (file) handleCoverFileSelect(file);
                        }}
                        onClick={() => coverFileInputRef.current?.click()}
                        className={`relative border-2 border-dashed p-5 rounded-none transition-all cursor-pointer flex flex-col items-center justify-center text-center gap-3 ${
                          isDragOverCover 
                            ? 'border-[#c084fc] bg-[#c084fc]/20 text-white' 
                            : coverPreviewUrl || newArt.imageUrl
                              ? 'border-[#c084fc]/60 bg-black'
                              : 'border-zinc-800 hover:border-[#c084fc]/60 bg-black/80'
                        }`}
                      >
                        {isAutoExtractingCover && (
                          <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center gap-2 z-20">
                            <Loader2 className="w-6 h-6 animate-spin text-[#c084fc]" />
                            <span className="text-xs font-mono text-purple-300">Capturando frame do vídeo para o Google...</span>
                          </div>
                        )}
                        <input 
                          ref={coverFileInputRef}
                          type="file" 
                          accept="image/*"
                          className="hidden" 
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleCoverFileSelect(file);
                          }}
                        />

                        {(coverPreviewUrl || newArt.imageUrl) ? (
                          <div className="flex flex-col sm:flex-row items-center gap-4 w-full">
                            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-none overflow-hidden border border-[#c084fc]/50 bg-black flex-shrink-0 relative group">
                              <img 
                                src={coverPreviewUrl || newArt.imageUrl} 
                                alt="Cover preview" 
                                className="w-full h-full object-cover" 
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                                <RefreshCw className="w-6 h-6 text-white" />
                              </div>
                            </div>
                            <div className="text-left space-y-1 flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                                  {coverPendingFile ? (language === 'pt' ? 'Nova Capa Selecionada' : 'New Cover Selected') : (language === 'pt' ? 'Capa Definida' : 'Cover Set')}
                                </span>
                              </div>
                              <p className="text-[11px] text-zinc-300 font-mono truncate">
                                {coverPendingFile ? coverPendingFile.name : (newArt.imageUrl || 'Capa pronta')}
                              </p>
                              <p className="text-[10px] text-zinc-500">
                                {language === 'pt' ? 'Arraste ou clique para trocar a imagem' : 'Drag or click to replace image'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="w-10 h-10 rounded-full bg-[#c084fc]/10 flex items-center justify-center text-[#c084fc]">
                              <Upload className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-white uppercase tracking-wider">
                                {language === 'pt' ? 'Arraste a foto da capa aqui ou clique para selecionar' : 'Drag cover image here or click to select'}
                              </p>
                              <p className="text-[10px] text-zinc-500 mt-1">
                                {language === 'pt' ? 'Suporta JPG, PNG, WEBP ou GIF (Ideal para Google Search)' : 'Supports JPG, PNG, WEBP or GIF (Ideal for Google Search)'}
                              </p>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Manual URL Input Fallback */}
                      <div className="pt-1">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest block mb-1">
                          {language === 'pt' ? 'Ou insira a URL da Imagem de Capa diretamente:' : 'Or enter Cover Image URL directly:'}
                        </label>
                        <input 
                          type="text" 
                          value={newArt.imageUrl || ''} 
                          onChange={e => {
                            setNewArt({...newArt, imageUrl: e.target.value});
                            setCoverPreviewUrl(e.target.value);
                          }} 
                          placeholder="https://... ou uploads/sua-capa.jpg"
                          className="w-full bg-black border border-zinc-800 p-3 text-white text-xs outline-none focus:border-[#c084fc] font-mono" 
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Descrição (PT)</label>
                        <textarea value={newArt.description?.pt} onChange={e => setNewArt({...newArt, description: {...newArt.description, pt: e.target.value}})} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs h-24 outline-none focus:border-[#c084fc] resize-none" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Descrição (EN)</label>
                        <textarea value={newArt.description?.en} onChange={e => setNewArt({...newArt, description: {...newArt.description, en: e.target.value}})} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs h-24 outline-none focus:border-[#c084fc] resize-none" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                          <CreditCard className="w-3.5 h-3.5 text-[#c084fc]" />
                          {language === 'pt' ? 'Link de Pagamento Stripe - USD/Geral (Opcional)' : 'Stripe Payment Link - USD/General (Optional)'}
                        </label>
                        <input 
                          type="text" 
                          value={newArt.stripeUrl || ''} 
                          onChange={e => setNewArt({...newArt, stripeUrl: e.target.value})} 
                          placeholder="https://buy.stripe.com/..."
                          className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc] font-mono" 
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                          <CreditCard className="w-3.5 h-3.5 text-[#c084fc]" />
                          {language === 'pt' ? 'Link de Pagamento Stripe - BRL/Brasil (Opcional)' : 'Stripe Payment Link - BRL/Brazil (Optional)'}
                        </label>
                        <input 
                          type="text" 
                          value={newArt.stripeUrlBr || ''} 
                          onChange={e => setNewArt({...newArt, stripeUrlBr: e.target.value})} 
                          placeholder="https://buy.stripe.com/..."
                          className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc] font-mono" 
                        />
                      </div>
                    </div>

                    <div className="space-y-2 bg-[#c084fc]/5 border border-[#c084fc]/20 p-5">
                      <label className="text-[9px] font-black text-[#c084fc] uppercase tracking-widest flex items-center gap-2">
                        <Download className="w-3.5 h-3.5" />
                        {language === 'pt' ? 'Link de Download (Liberado após Confirmação do Pagamento)' : 'Download Link (Released after Payment Confirmation)'}
                      </label>
                      <p className="text-[10px] text-zinc-400">
                        {language === 'pt' ? 'Ao preencher este link (ex: Google Drive, Mega, Dropbox), o comprador poderá baixar a arte assim que o pagamento for verificado no sistema.' : 'When filled (e.g. Google Drive, Mega, Dropbox), the buyer can download the artwork as soon as payment is verified.'}
                      </p>
                      <input 
                        type="text" 
                        value={newArt.downloadUrl || ''} 
                        onChange={e => setNewArt({...newArt, downloadUrl: e.target.value})} 
                        placeholder="https://drive.google.com/file/d/... ou https://mega.nz/..."
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#c084fc] font-mono" 
                      />
                    </div>

                    <div className="flex gap-4 pt-6 border-t border-white/5">
                      <button 
                        onClick={handleUploadRealArt} 
                        disabled={isUploading}
                        className="flex-1 bg-white hover:bg-[#c084fc] text-black hover:text-white py-5 font-black uppercase text-[10px] flex items-center justify-center gap-3 transition-all active:scale-95 shadow-xl"
                      >
                        {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {editingArt ? 'Atualizar Obra' : 'Sincronizar com Servidor'}
                      </button>
                      <button onClick={resetForm} className="px-10 border border-zinc-800 text-zinc-500 font-black uppercase text-[10px] hover:text-white hover:border-white transition-all">Cancelar</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {filteredAdminArts.length === 0 ? (
              <div className="py-20 text-center border border-dashed border-purple-900/30 bg-purple-950/5">
                <Search className="w-10 h-10 text-purple-400/40 mx-auto mb-3" />
                <p className="text-xs font-black uppercase text-purple-300 tracking-wider">
                  {language === 'en' ? 'No artworks found' : 'Nenhuma arte encontrada'}
                </p>
                <p className="text-[10px] text-zinc-500 mt-1">
                  {language === 'en' ? 'Try adjusting your search query or filters' : 'Tente ajustar sua busca ou filtros'}
                </p>
                {(gallerySearch || gallerySortBy !== 'default') && (
                  <button
                    onClick={() => {
                      setGallerySearch('');
                      setGallerySortBy('default');
                      setGallerySortOrder('desc');
                    }}
                    className="mt-4 px-4 py-2 bg-purple-950/40 border border-purple-800 text-purple-300 text-[10px] font-black uppercase hover:bg-purple-900/50 transition-all"
                  >
                    {language === 'en' ? 'Clear Filters' : 'Limpar Filtros'}
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                {filteredAdminArts.map(art => (
                  <div key={art.id} className="bg-zinc-900/40 p-4 border border-white/5 group relative">
                    <div className={`relative ${getAspectClass(art)} bg-black mb-4 overflow-hidden`}>
                      {isImageFormatUrl(art.imageUrl) ? (
                        <div className="absolute top-2 left-2 bg-emerald-950/90 border border-emerald-500/60 text-emerald-300 text-[7px] font-mono px-2 py-0.5 uppercase tracking-wider flex items-center gap-1 rounded shadow-md z-10" title="Capa JPG pronta para o Google Imagens">
                          <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
                          <span>Google OK</span>
                        </div>
                      ) : (
                        <div className="absolute top-2 left-2 bg-amber-950/90 border border-amber-500/70 text-amber-300 text-[7px] font-mono px-2 py-0.5 uppercase tracking-wider flex items-center gap-1 rounded shadow-md z-10 animate-pulse" title="Sem imagem JPG (Vídeo apenas). O Google Imagens não indexa vídeos!">
                          <AlertCircle className="w-2.5 h-2.5 text-amber-400" />
                          <span>Sem Capa JPG</span>
                        </div>
                      )}

                      {isVideoUrl(art.videoUrl || art.imageUrl) ? (
                        <video src={art.videoUrl || art.imageUrl} autoPlay loop muted className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                      ) : (
                        <img src={art.imageUrl} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                      )}
                      {art.notForSale && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-[8px] font-black px-2 py-1 uppercase tracking-widest z-10">NFS</div>
                      )}
                      
                      <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-2.5 opacity-0 group-hover:opacity-100 transition-opacity z-20 p-2">
                         {!isImageFormatUrl(art.imageUrl) && (
                           <button 
                             onClick={() => handleQuickExtractCover(art)} 
                             className="w-36 py-2 bg-amber-400 hover:bg-amber-300 text-black font-black uppercase text-[8px] flex items-center justify-center gap-1.5 transition-all shadow-lg"
                             title="Extrai um frame JPG do vídeo para indexar no Google Imagens"
                           >
                             <Sparkles className="w-3 h-3" /> Gerar Capa JPG
                           </button>
                         )}
                         <button onClick={() => handleEditClick(art)} className="w-36 py-2.5 bg-white text-black font-black uppercase text-[9px] flex items-center justify-center gap-2 hover:bg-[#c084fc] hover:text-white transition-all"><Edit2 className="w-3 h-3" /> Editar</button>
                         <button onClick={() => handleDeleteArt(art.id)} className="w-36 py-2.5 bg-zinc-900 text-red-500 border border-red-500/30 font-black uppercase text-[9px] flex items-center justify-center gap-2 hover:bg-red-500 hover:text-white transition-all"><Trash2 className="w-3 h-3" /> Excluir</button>
                      </div>
                    </div>
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-[10px] font-black uppercase truncate group-hover:text-[#c084fc] transition-colors">{art.title.pt}</h4>
                        <p className="text-zinc-600 text-[8px] font-black uppercase tracking-widest">{art.category}{art.subcategory ? ` > ${art.subcategory}` : ''}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className={`font-mono text-xs font-black ${art.notForSale ? 'text-zinc-700 italic' : 'text-[#c084fc]'}`}>
                          {art.notForSale ? 'NFS' : `$ ${art.price.toFixed(2)}`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="space-y-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-zinc-900/60 border border-white/5 p-8 backdrop-blur-md gap-6">
               <div className="flex-1">
                  <h3 className="text-white font-black uppercase text-sm tracking-widest">Sincronia Global de Capas</h3>
                  <p className="text-zinc-500 text-[9px] font-black uppercase tracking-[0.3em] mt-1">Publique no servidor ou baixe o JSON para upload manual</p>
               </div>
               <div className="flex flex-wrap gap-4">
                 <button onClick={handleDownloadCoversJSON} className="bg-zinc-800 text-white px-8 py-5 text-[10px] font-black uppercase flex items-center gap-4 border border-white/5 hover:bg-[#c084fc]"><Download className="w-4 h-4" /> Baixar category_covers.json</button>
                 <button onClick={handleGlobalSyncCovers} disabled={isSyncing} className="bg-white hover:bg-[#c084fc] text-black hover:text-white px-10 py-5 text-[10px] font-black uppercase flex items-center gap-4 transition-all shadow-xl active:scale-95 disabled:opacity-20">{isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />} Forçar Sincronização Direta</button>
               </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Object.values(ArtCategory).map(cat => (
                <div key={cat} className="bg-zinc-900/40 border border-white/5 p-6 flex flex-col gap-6 group hover:border-[#c084fc]/20 transition-all">
                  <div className="aspect-video bg-black overflow-hidden border border-white/5 relative">
                    {isVideoUrl(categoryCovers[cat]) ? (
                      <video src={categoryCovers[cat]} autoPlay loop muted className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <img src={categoryCovers[cat] || MASTER_FALLBACK_IMAGE} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><h4 className="text-white font-black uppercase text-xs tracking-widest">{cat}</h4></div>
                  </div>
                  
                  {editingCategory === cat ? (
                    <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                       <input type="text" placeholder="https://..." value={catCoverPreview || ''} onChange={(e) => setCatCoverPreview(e.target.value)} className="w-full bg-black border border-zinc-800 p-3 text-white text-[10px] outline-none focus:border-[#c084fc]" />
                       <input ref={catFileInputRef} type="file" className="hidden" onChange={(e) => {
                           const file = e.target.files?.[0];
                           if (file) {
                             setCatPendingFile(file);
                             setCatCoverPreview(URL.createObjectURL(file));
                           }
                       }} accept="image/*,video/mp4" />
                       <div className="flex gap-2">
                          <button onClick={() => catFileInputRef.current?.click()} className="flex-1 bg-zinc-800 text-white py-3 text-[9px] font-black uppercase hover:bg-zinc-700 transition-all">Upload</button>
                          <button onClick={handleSaveCategoryCover} disabled={isSyncing} className="flex-1 bg-[#c084fc] text-white py-3 text-[9px] font-black uppercase hover:bg-white hover:text-[#c084fc] transition-all flex items-center justify-center gap-2">{isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Ok</button>
                          <button onClick={() => { setEditingCategory(null); setCatPendingFile(null); setCatCoverPreview(null); }} className="px-4 bg-zinc-950 text-zinc-500 py-3 text-[9px] font-black uppercase hover:text-white">X</button>
                       </div>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingCategory(cat); setCatCoverPreview(categoryCovers[cat]); }} className="w-full py-4 border border-zinc-800 text-zinc-500 font-black uppercase text-[10px] hover:text-white hover:border-[#c084fc] transition-all">Editar Capa</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Fluxo de Pedidos</h3>
                <p className="text-zinc-600 text-[8px] font-black uppercase tracking-[0.4em]">Gestão de vendas e entregas</p>
              </div>
              <button onClick={refreshOrders} className="bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-[#c084fc] px-6 py-3 flex items-center gap-3 text-[10px] font-black uppercase transition-all">
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} /> Sincronizar
              </button>
            </div>
            <div className="grid gap-4">
              {safeOrders.map(order => (
                <div key={order.id} className="bg-zinc-900/40 border border-white/5 flex flex-col group hover:border-[#c084fc]/30 transition-all overflow-hidden">
                  <div onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)} className="p-6 flex flex-wrap justify-between items-center cursor-pointer hover:bg-white/5 transition-colors">
                    <div className="flex flex-wrap gap-6 sm:gap-10">
                      <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-1">Identificador</p><p className="text-white font-mono text-xs">{order.id}</p></div>
                      <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-1">Membro</p><p className="text-white font-black uppercase text-xs">{order.userName}</p></div>
                      <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-1">Data / Horário (BR)</p><p className="text-zinc-300 font-mono text-xs">{formatBrazilDateTime(order.date)}</p></div>
                      <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-1">Investimento</p><p className="text-[#c084fc] font-mono text-xs">$ {order.total.toFixed(2)}</p></div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-6">
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleOpenChatWithUser(order.userEmail, order.userName); }}
                        className="px-3 py-2 bg-[#c084fc]/10 hover:bg-[#c084fc] text-[#c084fc] hover:text-white border border-[#c084fc]/30 flex items-center gap-2 text-[9px] font-black uppercase transition-all"
                        title={language === 'en' ? 'Open Chat with Customer' : 'Abrir Chat com Cliente'}
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">{language === 'en' ? 'Chat' : 'Abrir Chat'}</span>
                      </button>
                      <select value={order.status} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); database.updateOrderStatus(order.id, e.target.value as OrderStatus).then(refreshOrders); }} className={`bg-black border border-zinc-800 text-[10px] font-black uppercase px-6 py-2 outline-none focus:border-[#c084fc] transition-all ${order.status === OrderStatus.COMPLETED ? 'text-green-500 border-green-500/20' : 'text-[#c084fc] border-[#c084fc]/20'}`}>
                        {Object.values(OrderStatus).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order.id); }}
                        className="p-2 bg-zinc-950 text-zinc-600 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedOrderId === order.id ? <X className="w-5 h-5 text-zinc-500" /> : <ChevronDown className="w-5 h-5 text-zinc-500" />}
                    </div>
                  </div>

                  {expandedOrderId === order.id && (
                    <div className="p-8 bg-black/40 border-t border-white/5 animate-in slide-in-from-top-4 duration-300">
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
                          <div className="space-y-4">
                             <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2 border-b border-white/5 pb-2"><UserCheck className="w-3.5 h-3.5" /> Informações do Cliente</h4>
                             <div className="grid grid-cols-2 gap-4">
                                <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest">E-mail</p><p className="text-white text-[11px] truncate">{order.userEmail}</p></div>
                                <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest">Data / Horário (BR)</p><p className="text-white text-[11px]">{formatBrazilDateTime(order.date)}</p></div>
                             </div>
                             <button
                                onClick={() => handleOpenChatWithUser(order.userEmail, order.userName)}
                                className="bg-[#c084fc] hover:bg-white text-black font-black text-[10px] uppercase tracking-wider px-5 py-2.5 flex items-center gap-2 transition-all border border-[#c084fc] active:scale-95 mt-2"
                             >
                                <MessageSquare className="w-4 h-4" />
                                <span>{language === 'en' ? `Open Chat with ${order.userName || 'Client'}` : `Abrir Chat com ${order.userName || 'Cliente'}`}</span>
                             </button>
                          </div>
                       </div>
                       <div className="space-y-6">
                          <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2 border-b border-white/5 pb-2"><Box className="w-3.5 h-3.5" /> Itens e Especificações</h4>
                          {order.items.map((item, idx) => (
                            <div key={idx} className="bg-zinc-950/50 p-6 border border-white/5 space-y-6">
                               <div className="flex items-center gap-6">
                                  <div className="w-16 h-16 bg-black border border-white/5"><img src={item.imageUrl} className="w-full h-full object-cover" /></div>
                                  <div><h5 className="text-white font-black uppercase text-xs">{item.title.pt}</h5><p className="text-zinc-600 text-[9px] font-black uppercase tracking-widest">{item.category}</p></div>
                               </div>
                               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                  {item.customSignature && (
                                    <div className="bg-black p-4 border border-white/5">
                                      <span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Assinatura</span>
                                      <p className="artistic-font text-lg leading-none" style={{ color: item.sigColor || '#c084fc' }}>{item.customSignature}</p>
                                    </div>
                                  )}
                                  {item.sigColor && (
                                    <div className="bg-black p-4 border border-white/5">
                                      <span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Cor Selecionada</span>
                                      <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: item.sigColor }} />
                                        <span className="text-white font-mono text-[10px]">{item.sigColor.toUpperCase()}</span>
                                      </div>
                                    </div>
                                  )}
                                  {item.discord && <div className="bg-black p-4 border border-white/5"><span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Discord</span><p className="text-white font-mono text-[11px]">{item.discord}</p></div>}
                                  {item.steamUrl && <div className="bg-black p-4 border border-white/5"><span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Steam Link</span><a href={item.steamUrl} target="_blank" rel="noopener noreferrer" className="text-zinc-400 font-mono text-[10px] hover:text-[#c084fc] transition-colors truncate block">{item.steamUrl}</a></div>}
                                  {item.dafontLink && (
                                    <div className="bg-black p-4 border border-white/5">
                                      <span className="text-[8px] text-[#c084fc] font-black uppercase block mb-1">Link da Fonte (Dafont)</span>
                                      {item.dafontLink.startsWith('http') ? (
                                        <a href={item.dafontLink} target="_blank" rel="noopener noreferrer" className="text-white font-mono text-[10px] hover:text-[#c084fc] transition-colors underline truncate block">{item.dafontLink}</a>
                                      ) : (
                                        <p className="text-white font-mono text-[11px]">{item.dafontLink}</p>
                                      )}
                                    </div>
                                  )}
                                  {item.customFont && item.customFont !== 'Custom' && (
                                    <div className="bg-black p-4 border border-white/5">
                                      <span className="text-[8px] text-[#c084fc] font-black uppercase block mb-1">Fonte Selecionada</span>
                                      <p className="text-white font-mono text-[11px]">{item.customFont}</p>
                                    </div>
                                  )}
                                  {((item.sigPosX !== undefined && item.sigPosX !== 0) || (item.sigPosY !== undefined && item.sigPosY !== 60)) && (
                                    <div className="bg-black p-4 border border-white/5"><span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Posição / Escala</span><p className="text-white font-mono text-[10px]">X: {item.sigPosX} | Y: {item.sigPosY} | S: {item.sigScale}x</p></div>
                                  )}
                                  {item.notes && (
                                    <div className="bg-black p-4 border border-white/5 col-span-1 sm:col-span-2 lg:col-span-3">
                                      <span className="text-[8px] text-[#c084fc] font-black uppercase block mb-1">Observações do Cliente</span>
                                      <p className="text-zinc-300 font-sans text-xs whitespace-pre-wrap">{item.notes}</p>
                                    </div>
                                  )}
                               </div>
                            </div>
                          ))}
                       </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-10 animate-in fade-in duration-500">
            {/* Header & Quick Global Stats */}
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-zinc-900/60 border border-white/5 p-8 backdrop-blur-md">
                <div>
                  <h3 className="text-white font-black uppercase text-sm tracking-widest flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#c084fc]" />
                    {language === 'en' ? 'Users & Global Sales Directory' : language === 'es' ? 'Directorio de Usuarios y Ventas Globales' : language === 'ru' ? 'Пользователи и Продажи' : 'Gestão de Usuários & Vendas Globais'}
                  </h3>
                  <p className="text-zinc-500 text-[9px] font-black uppercase tracking-[0.3em] mt-1">
                    {language === 'en' 
                      ? 'Customer Purchase Histories, Client Registry & Total Sold Items' 
                      : 'Histórico de Compras por Usuário, Lista de Clientes e Itens Vendidos'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button 
                    onClick={() => { refreshUsers(); refreshOrders(); }} 
                    disabled={isLoadingUsers || isSyncing}
                    className="bg-zinc-800 hover:bg-zinc-700 text-white px-5 py-3 text-[10px] font-black uppercase flex items-center gap-2 border border-white/5 transition-all"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoadingUsers || isSyncing ? 'animate-spin text-[#c084fc]' : ''}`} />
                    <span>{language === 'en' ? 'Sync Data' : 'Sincronizar'}</span>
                  </button>
                  <button 
                    onClick={handleExportSoldItemsCSV}
                    className="bg-zinc-800 hover:bg-[#c084fc] text-white px-5 py-3 text-[10px] font-black uppercase flex items-center gap-2 border border-white/5 transition-all"
                    title={language === 'en' ? 'Export CSV Report' : 'Exportar Relatório CSV'}
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5" />
                    <span>CSV</span>
                  </button>
                  <button 
                    onClick={handleExportSoldItemsJSON}
                    className="bg-zinc-800 hover:bg-[#c084fc] text-white px-5 py-3 text-[10px] font-black uppercase flex items-center gap-2 border border-white/5 transition-all"
                    title={language === 'en' ? 'Export JSON Report' : 'Exportar Relatório JSON'}
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>JSON</span>
                  </button>
                </div>
              </div>

              {/* 4 Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Users Card */}
                <div className="bg-zinc-950/70 border border-white/5 p-6 relative overflow-hidden group hover:border-[#c084fc]/40 transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-[8px] font-black uppercase tracking-widest text-zinc-500">
                      {language === 'en' ? 'Registered Users' : 'Total de Usuários'}
                    </span>
                    <div className="p-2 bg-[#c084fc]/10 text-[#c084fc]">
                      <Users className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="text-2xl font-black text-white tracking-tight">
                    {salesMetrics.totalUsersCount}
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-[9px] font-mono text-zinc-400">
                    <span className="text-green-400 font-bold">{salesMetrics.uniqueBuyersCount}</span>
                    <span>{language === 'en' ? 'active buyers' : 'compradores ativos'}</span>
                  </div>
                </div>

                {/* Total Sold Items Card */}
                <div className="bg-zinc-950/70 border border-white/5 p-6 relative overflow-hidden group hover:border-[#c084fc]/40 transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-[8px] font-black uppercase tracking-widest text-zinc-500">
                      {language === 'en' ? 'Total Items Sold' : 'Total de Itens Vendidos'}
                    </span>
                    <div className="p-2 bg-[#c084fc]/10 text-[#c084fc]">
                      <PackageCheck className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="text-2xl font-black text-white tracking-tight">
                    {salesMetrics.totalItemsCount} <span className="text-xs text-zinc-500 font-normal">{language === 'en' ? 'units' : 'unidades'}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-[9px] font-mono text-zinc-400">
                    <span>{salesMetrics.totalOrdersCount} {language === 'en' ? 'total orders' : 'pedidos totais'}</span>
                  </div>
                </div>

                {/* Gross Revenue Card */}
                <div className="bg-zinc-950/70 border border-white/5 p-6 relative overflow-hidden group hover:border-[#c084fc]/40 transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-[8px] font-black uppercase tracking-widest text-zinc-500">
                      {language === 'en' ? 'Gross Revenue' : 'Faturamento Total'}
                    </span>
                    <div className="p-2 bg-green-500/10 text-green-400">
                      <DollarSign className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="text-2xl font-black text-green-400 font-mono tracking-tight">
                    $ {salesMetrics.totalRevenue.toFixed(2)}
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-[9px] font-mono text-zinc-400">
                    <span className="text-zinc-500">$ {salesMetrics.completedRevenue.toFixed(2)} {language === 'en' ? 'completed' : 'concluídos'}</span>
                  </div>
                </div>

                {/* Avg Ticket Card */}
                <div className="bg-zinc-950/70 border border-white/5 p-6 relative overflow-hidden group hover:border-[#c084fc]/40 transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-[8px] font-black uppercase tracking-widest text-zinc-500">
                      {language === 'en' ? 'Average Order Value' : 'Ticket Médio'}
                    </span>
                    <div className="p-2 bg-[#c084fc]/10 text-[#c084fc]">
                      <Receipt className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="text-2xl font-black text-white font-mono tracking-tight">
                    $ {salesMetrics.avgTicket.toFixed(2)}
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-[9px] font-mono text-zinc-400">
                    <span className="text-[#c084fc] font-bold">{salesMetrics.completedOrdersCount}</span>
                    <span>{language === 'en' ? 'delivered orders' : 'pedidos entregues'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-Navigation Tabs */}
            <div className="flex border-b border-white/10 gap-2 pb-0">
              <button
                onClick={() => setUsersSubTab('users')}
                className={`px-6 py-4 text-xs font-black uppercase tracking-wider flex items-center gap-2.5 transition-all border-b-2 ${
                  usersSubTab === 'users'
                    ? 'border-[#c084fc] text-white bg-[#c084fc]/10'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-white/5'
                }`}
              >
                <Users className="w-4 h-4 text-[#c084fc]" />
                <span>{language === 'en' ? 'Users & Purchase Histories' : 'Usuários & Histórico de Compras'}</span>
                <span className="ml-1.5 px-2 py-0.5 text-[9px] rounded-full bg-zinc-800 text-zinc-400">
                  {filteredUsers.length}
                </span>
              </button>

              <button
                onClick={() => setUsersSubTab('sold_items')}
                className={`px-6 py-4 text-xs font-black uppercase tracking-wider flex items-center gap-2.5 transition-all border-b-2 ${
                  usersSubTab === 'sold_items'
                    ? 'border-[#c084fc] text-white bg-[#c084fc]/10'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-white/5'
                }`}
              >
                <ShoppingBag className="w-4 h-4 text-[#c084fc]" />
                <span>{language === 'en' ? 'All Sold Items History' : 'Histórico Total de Itens Vendidos'}</span>
                <span className="ml-1.5 px-2 py-0.5 text-[9px] rounded-full bg-zinc-800 text-zinc-400">
                  {filteredSoldItems.length}
                </span>
              </button>

              <button
                onClick={() => setUsersSubTab('ranking')}
                className={`px-6 py-4 text-xs font-black uppercase tracking-wider flex items-center gap-2.5 transition-all border-b-2 ${
                  usersSubTab === 'ranking'
                    ? 'border-[#c084fc] text-white bg-[#c084fc]/10'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-white/5'
                }`}
              >
                <Award className="w-4 h-4 text-amber-400" />
                <span>{language === 'en' ? 'Top Best-Sellers' : 'Mais Vendidos (Ranking)'}</span>
                <span className="ml-1.5 px-2 py-0.5 text-[9px] rounded-full bg-zinc-800 text-zinc-400">
                  {topSellingRanking.length}
                </span>
              </button>
            </div>

            {/* ===================== SUB-VIEW 1: USERS & PURCHASE HISTORIES ===================== */}
            {usersSubTab === 'users' && (
              <div className="space-y-8">
                {/* Search & Filter Bar */}
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                    <input
                      type="text"
                      placeholder={language === 'en' ? 'Search user by name or email...' : 'Buscar usuário por nome ou e-mail...'}
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full bg-black border border-zinc-800 pl-11 pr-4 py-3.5 text-white text-xs outline-none focus:border-[#c084fc] transition-colors"
                    />
                    {userSearchQuery && (
                      <button 
                        onClick={() => setUserSearchQuery('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Filter Type */}
                  <div className="flex bg-zinc-950 border border-zinc-800 p-1">
                    {[
                      { id: 'all', label: language === 'en' ? 'All Users' : 'Todos' },
                      { id: 'buyers', label: language === 'en' ? 'Buyers Only' : 'Compradores' },
                      { id: 'non-buyers', label: language === 'en' ? 'No Purchases' : 'Sem Compras' },
                    ].map(f => (
                      <button
                        key={f.id}
                        onClick={() => setUserFilter(f.id as any)}
                        className={`px-4 py-2 text-[10px] font-black uppercase transition-all ${
                          userFilter === f.id ? 'bg-[#c084fc] text-white' : 'text-zinc-500 hover:text-white'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>

                  {/* Sort Selector */}
                  <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 px-3 py-1">
                    <ArrowUpDown className="w-3.5 h-3.5 text-zinc-500" />
                    <select
                      value={userSortBy}
                      onChange={(e) => setUserSortBy(e.target.value as any)}
                      className="bg-transparent text-white text-[10px] font-black uppercase outline-none cursor-pointer"
                    >
                      <option value="spent_desc" className="bg-zinc-900">{language === 'en' ? 'Highest Spent ($)' : 'Maior Gasto ($)'}</option>
                      <option value="orders_desc" className="bg-zinc-900">{language === 'en' ? 'Most Orders' : 'Mais Pedidos'}</option>
                      <option value="recent" className="bg-zinc-900">{language === 'en' ? 'Most Recent' : 'Mais Recente'}</option>
                      <option value="name_asc" className="bg-zinc-900">{language === 'en' ? 'Alphabetical (A-Z)' : 'Nome (A-Z)'}</option>
                    </select>
                  </div>
                </div>

                {/* Users List Grid */}
                {filteredUsers.length === 0 ? (
                  <div className="py-20 text-center border border-dashed border-zinc-800 bg-zinc-950/40">
                    <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-xs font-black uppercase text-zinc-400">
                      {language === 'en' ? 'No users found matching your criteria' : 'Nenhum usuário encontrado com os filtros atuais'}
                    </p>
                    {(userSearchQuery || userFilter !== 'all') && (
                      <button
                        onClick={() => { setUserSearchQuery(''); setUserFilter('all'); }}
                        className="mt-4 px-4 py-2 bg-zinc-900 border border-zinc-700 text-white text-[10px] font-black uppercase hover:bg-zinc-800 transition-all"
                      >
                        {language === 'en' ? 'Clear Filters' : 'Limpar Filtros'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredUsers.map(({ user, orders: userOrders, totalSpent, itemsCount, lastOrderDate, completedOrdersCount, pendingOrdersCount }) => {
                      const isSelected = selectedUserEmail?.toLowerCase() === user.email.toLowerCase();
                      const hasPurchases = userOrders.length > 0;
                      
                      return (
                        <div
                          key={user.email}
                          className={`bg-zinc-950/60 border transition-all p-6 flex flex-col justify-between group ${
                            isSelected ? 'border-[#c084fc] shadow-lg shadow-[#c084fc]/10 bg-zinc-900/40' : 'border-white/5 hover:border-zinc-700'
                          }`}
                        >
                          <div>
                            {/* User Header */}
                            <div className="flex items-start justify-between gap-4 mb-4">
                              <div className="flex items-center gap-3 min-w-0">
                                {user.avatarUrl ? (
                                  <img src={user.avatarUrl} alt={user.name} className="w-10 h-10 rounded-full object-cover border border-[#c084fc]/30 flex-shrink-0" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c084fc]/30 to-purple-900/50 border border-[#c084fc]/30 flex items-center justify-center text-white font-black text-xs flex-shrink-0">
                                    {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <h4 className="text-white font-black text-xs uppercase truncate group-hover:text-[#c084fc] transition-colors">
                                    {user.name || user.email.split('@')[0]}
                                  </h4>
                                  <p className="text-zinc-500 font-mono text-[9px] truncate">{user.email}</p>
                                </div>
                              </div>

                              {/* Buyer Status Badge */}
                              <div>
                                {userOrders.length >= 3 ? (
                                  <span className="px-2 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[8px] font-black uppercase tracking-wider rounded">
                                    VIP
                                  </span>
                                ) : hasPurchases ? (
                                  <span className="px-2 py-1 bg-green-500/10 border border-green-500/30 text-green-400 text-[8px] font-black uppercase tracking-wider rounded">
                                    Buyer
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-zinc-800 text-zinc-500 text-[8px] font-black uppercase tracking-wider rounded">
                                    Member
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* User Stats Grid */}
                            <div className="grid grid-cols-3 gap-2 py-3 border-y border-white/5 my-4 bg-black/30 p-3">
                              <div>
                                <span className="text-[7px] text-zinc-600 font-black uppercase block tracking-wider">
                                  {language === 'en' ? 'Total Spent' : 'Total Gasto'}
                                </span>
                                <p className="text-[#c084fc] font-mono text-xs font-black mt-0.5">
                                  $ {totalSpent.toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <span className="text-[7px] text-zinc-600 font-black uppercase block tracking-wider">
                                  {language === 'en' ? 'Orders' : 'Pedidos'}
                                </span>
                                <p className="text-white font-mono text-xs font-bold mt-0.5">
                                  {userOrders.length}
                                </p>
                              </div>
                              <div>
                                <span className="text-[7px] text-zinc-600 font-black uppercase block tracking-wider">
                                  {language === 'en' ? 'Items Bought' : 'Itens'}
                                </span>
                                <p className="text-white font-mono text-xs font-bold mt-0.5">
                                  {itemsCount}
                                </p>
                              </div>
                            </div>

                            {/* Last Activity */}
                            {lastOrderDate && (
                              <div className="text-[8px] text-zinc-500 font-mono mb-4 flex items-center gap-1.5">
                                <Clock className="w-3 h-3 text-zinc-600" />
                                <span>{language === 'en' ? 'Last purchase:' : 'Última compra:'} {lastOrderDate}</span>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
                            <button
                              onClick={() => {
                                setSelectedUserEmail(isSelected ? null : user.email);
                              }}
                              className={`flex-1 py-2.5 px-3 text-[9px] font-black uppercase flex items-center justify-center gap-1.5 transition-all border ${
                                isSelected 
                                  ? 'bg-[#c084fc] text-white border-[#c084fc]' 
                                  : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800 hover:border-zinc-600'
                              }`}
                            >
                              <ShoppingBag className="w-3 h-3" />
                              <span>{language === 'en' ? (isSelected ? 'Close History' : 'Purchase History') : (isSelected ? 'Fechar Histórico' : 'Histórico de Compras')}</span>
                            </button>

                            <button
                              onClick={() => {
                                handleOpenChatWithUser(user.email, user.name || user.email.split('@')[0]);
                              }}
                              className="p-2.5 bg-zinc-900 hover:bg-[#c084fc]/20 text-[#c084fc] border border-zinc-800 hover:border-[#c084fc]/40 transition-all"
                              title={language === 'en' ? 'Open Live Chat' : 'Abrir Atendimento / Chat'}
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </button>

                            <button
                              onClick={() => handleDeleteUser(user.email)}
                              className="p-2.5 bg-zinc-900 hover:bg-red-500/20 text-zinc-600 hover:text-red-400 border border-zinc-800 hover:border-red-500/40 transition-all"
                              title={language === 'en' ? 'Delete User' : 'Excluir Usuário'}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Selected User Full Purchase History Modal / Drawer */}
                {selectedUserEmail && (
                  <div className="bg-zinc-950 border-2 border-[#c084fc]/50 p-8 md:p-10 space-y-8 animate-in slide-in-from-top-4 duration-300">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-white/10 pb-6">
                      <div>
                        <div className="flex items-center gap-3">
                          <h4 className="text-white font-black uppercase text-base tracking-wider flex items-center gap-2">
                            <ShoppingBag className="w-5 h-5 text-[#c084fc]" />
                            {language === 'en' ? 'Purchase History for' : 'Histórico Completo de Compras de'}
                            <span className="text-[#c084fc]">
                              {userPurchaseStats.find(u => u.user.email.toLowerCase() === selectedUserEmail.toLowerCase())?.user.name || selectedUserEmail}
                            </span>
                          </h4>
                        </div>
                        <p className="text-zinc-500 font-mono text-[10px] mt-1">{selectedUserEmail}</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            const u = userPurchaseStats.find(u => u.user.email.toLowerCase() === selectedUserEmail.toLowerCase());
                            handleOpenChatWithUser(selectedUserEmail, u?.user.name || selectedUserEmail.split('@')[0]);
                          }}
                          className="px-4 py-2.5 bg-[#c084fc]/10 hover:bg-[#c084fc] text-[#c084fc] hover:text-white border border-[#c084fc]/30 text-[10px] font-black uppercase transition-all flex items-center gap-2"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>{language === 'en' ? 'Chat with Customer' : 'Abrir Atendimento'}</span>
                        </button>
                        <button
                          onClick={() => setSelectedUserEmail(null)}
                          className="p-2.5 bg-zinc-900 text-zinc-500 hover:text-white border border-zinc-800 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Orders by this user */}
                    {(() => {
                      const userEntry = userPurchaseStats.find(u => u.user.email.toLowerCase() === selectedUserEmail.toLowerCase());
                      const userOrders = userEntry?.orders || [];

                      if (userOrders.length === 0) {
                        return (
                          <div className="py-12 text-center border border-dashed border-zinc-800 bg-black/40">
                            <ShoppingBag className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                            <p className="text-xs text-zinc-500 font-black uppercase">
                              {language === 'en' ? 'This user has no purchase history yet.' : 'Este usuário ainda não realizou compras no site.'}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-6">
                          {userOrders.map(order => (
                            <div key={order.id} className="bg-zinc-900/60 border border-white/10 overflow-hidden">
                              {/* Order Header Summary */}
                              <div className="p-6 flex flex-wrap justify-between items-center bg-black/40 gap-4">
                                <div className="flex flex-wrap gap-8 items-center">
                                  <div>
                                    <span className="text-[7px] text-zinc-600 font-black uppercase block tracking-widest">Pedido ID</span>
                                    <span className="text-white font-mono text-xs font-bold">{order.id}</span>
                                  </div>
                                  <div>
                                    <span className="text-[7px] text-zinc-600 font-black uppercase block tracking-widest">Data / Horário (BR)</span>
                                    <span className="text-zinc-300 font-mono text-xs">{formatBrazilDateTime(order.date)}</span>
                                  </div>
                                  <div>
                                    <span className="text-[7px] text-zinc-600 font-black uppercase block tracking-widest">Total</span>
                                    <span className="text-[#c084fc] font-mono text-sm font-black">$ {order.total.toFixed(2)}</span>
                                  </div>
                                  <div>
                                    <span className="text-[7px] text-zinc-600 font-black uppercase block tracking-widest">Itens</span>
                                    <span className="text-white font-mono text-xs">{(order.items || []).length}</span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-3">
                                  <select 
                                    value={order.status} 
                                    onChange={(e) => {
                                      database.updateOrderStatus(order.id, e.target.value as OrderStatus).then(refreshOrders);
                                    }} 
                                    className={`bg-black border border-zinc-800 text-[10px] font-black uppercase px-4 py-2 outline-none focus:border-[#c084fc] transition-all ${
                                      order.status === OrderStatus.COMPLETED ? 'text-green-400 border-green-500/30' : 'text-[#c084fc] border-[#c084fc]/30'
                                    }`}
                                  >
                                    {Object.values(OrderStatus).map(s => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                </div>
                              </div>

                              {/* Order Items List */}
                              <div className="p-6 space-y-4">
                                {(order.items || []).map((item, idx) => (
                                  <div key={idx} className="bg-black/60 p-4 border border-white/5 space-y-4">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                      <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 bg-zinc-900 border border-white/5 overflow-hidden flex-shrink-0">
                                          <img src={item.imageUrl} alt={item.title?.pt} className="w-full h-full object-cover" />
                                        </div>
                                        <div>
                                          <h5 className="text-white font-black text-xs uppercase">{item.title?.pt || item.title?.en}</h5>
                                          <p className="text-zinc-500 text-[8px] font-black uppercase tracking-widest">{item.category} {item.subcategory ? `> ${item.subcategory}` : ''}</p>
                                          <span className="text-[#c084fc] font-mono text-xs font-bold mt-1 block">
                                            $ {item.price.toFixed(2)} {item.quantity && item.quantity > 1 ? `x ${item.quantity}` : ''}
                                          </span>
                                        </div>
                                      </div>

                                      {item.downloadUrl && (
                                        <a
                                          href={item.downloadUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-4 py-2 bg-zinc-800 hover:bg-[#c084fc] text-white text-[9px] font-black uppercase flex items-center gap-2 transition-all"
                                        >
                                          <Download className="w-3.5 h-3.5" />
                                          <span>Download Obra</span>
                                        </a>
                                      )}
                                    </div>

                                    {/* Custom Specifications if any */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-[9px] pt-3 border-t border-white/5">
                                      {item.customSignature && (
                                        <div className="bg-zinc-950 p-2.5 border border-white/5">
                                          <span className="text-[7px] text-zinc-500 uppercase block font-black">Assinatura</span>
                                          <p className="artistic-font text-base" style={{ color: item.sigColor || '#c084fc' }}>{item.customSignature}</p>
                                        </div>
                                      )}
                                      {item.discord && (
                                        <div className="bg-zinc-950 p-2.5 border border-white/5">
                                          <span className="text-[7px] text-zinc-500 uppercase block font-black">Discord</span>
                                          <p className="text-white font-mono">{item.discord}</p>
                                        </div>
                                      )}
                                      {item.steamUrl && (
                                        <div className="bg-zinc-950 p-2.5 border border-white/5">
                                          <span className="text-[7px] text-zinc-500 uppercase block font-black">Steam</span>
                                          <a href={item.steamUrl} target="_blank" rel="noopener noreferrer" className="text-[#c084fc] hover:underline font-mono truncate block">{item.steamUrl}</a>
                                        </div>
                                      )}
                                      {item.dafontLink && (
                                        <div className="bg-zinc-950 p-2.5 border border-white/5">
                                          <span className="text-[7px] text-zinc-500 uppercase block font-black">Fonte Dafont</span>
                                          <a href={item.dafontLink} target="_blank" rel="noopener noreferrer" className="text-[#c084fc] hover:underline font-mono truncate block">{item.dafontLink}</a>
                                        </div>
                                      )}
                                      {item.notes && (
                                        <div className="bg-zinc-950 p-2.5 border border-white/5 col-span-1 sm:col-span-2">
                                          <span className="text-[7px] text-zinc-500 uppercase block font-black">Observações</span>
                                          <p className="text-zinc-300">{item.notes}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* ===================== SUB-VIEW 2: ALL SOLD ITEMS HISTORY ===================== */}
            {usersSubTab === 'sold_items' && (
              <div className="space-y-8">
                {/* Search & Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Search bar */}
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                    <input
                      type="text"
                      placeholder={language === 'en' ? 'Search sold item, client or order...' : 'Buscar item, cliente ou pedido...'}
                      value={soldItemsSearch}
                      onChange={(e) => setSoldItemsSearch(e.target.value)}
                      className="w-full bg-black border border-zinc-800 pl-11 pr-4 py-3 text-white text-xs outline-none focus:border-[#c084fc] transition-colors"
                    />
                    {soldItemsSearch && (
                      <button 
                        onClick={() => setSoldItemsSearch('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Category Filter */}
                  <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 px-3 py-2">
                    <Filter className="w-3.5 h-3.5 text-zinc-500" />
                    <select
                      value={soldItemsCategory}
                      onChange={(e) => setSoldItemsCategory(e.target.value)}
                      className="w-full bg-transparent text-white text-[10px] font-black uppercase outline-none cursor-pointer"
                    >
                      <option value="all" className="bg-zinc-900">{language === 'en' ? 'All Categories' : 'Todas as Categorias'}</option>
                      {Object.values(ArtCategory).map(c => (
                        <option key={c} value={c} className="bg-zinc-900">{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Order Status Filter */}
                  <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 px-3 py-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-zinc-500" />
                    <select
                      value={soldItemsStatus}
                      onChange={(e) => setSoldItemsStatus(e.target.value)}
                      className="w-full bg-transparent text-white text-[10px] font-black uppercase outline-none cursor-pointer"
                    >
                      <option value="all" className="bg-zinc-900">{language === 'en' ? 'All Statuses' : 'Todos os Status'}</option>
                      {Object.values(OrderStatus).map(s => (
                        <option key={s} value={s} className="bg-zinc-900">{s}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sort Order */}
                  <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 px-3 py-2">
                    <ArrowUpDown className="w-3.5 h-3.5 text-zinc-500" />
                    <select
                      value={soldItemsSort}
                      onChange={(e) => setSoldItemsSort(e.target.value as any)}
                      className="w-full bg-transparent text-white text-[10px] font-black uppercase outline-none cursor-pointer"
                    >
                      <option value="recent" className="bg-zinc-900">{language === 'en' ? 'Most Recent' : 'Mais Recente'}</option>
                      <option value="price_desc" className="bg-zinc-900">{language === 'en' ? 'Highest Price ($)' : 'Maior Valor ($)'}</option>
                      <option value="price_asc" className="bg-zinc-900">{language === 'en' ? 'Lowest Price ($)' : 'Menor Valor ($)'}</option>
                    </select>
                  </div>
                </div>

                {/* Sold Items Table / Cards */}
                {filteredSoldItems.length === 0 ? (
                  <div className="py-20 text-center border border-dashed border-zinc-800 bg-zinc-950/40">
                    <ShoppingBag className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-xs font-black uppercase text-zinc-400">
                      {language === 'en' ? 'No sold items found matching filters' : 'Nenhum item vendido encontrado com esses critérios'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredSoldItems.map((sold) => (
                      <div
                        key={sold.uniqueKey}
                        className="bg-zinc-950/60 border border-white/5 p-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 group hover:border-[#c084fc]/30 transition-all"
                      >
                        {/* Media and Item Info */}
                        <div className="flex items-center gap-5 min-w-0">
                          <div className="w-16 h-16 bg-black border border-white/10 overflow-hidden flex-shrink-0 relative group/thumb">
                            {isVideoUrl(sold.videoUrl || sold.imageUrl) ? (
                              <video src={sold.videoUrl || sold.imageUrl} autoPlay loop muted className="w-full h-full object-cover" />
                            ) : (
                              <img src={sold.imageUrl || MASTER_FALLBACK_IMAGE} alt={sold.itemTitle} className="w-full h-full object-cover" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-white font-black text-sm uppercase truncate group-hover:text-[#c084fc] transition-colors">
                              {sold.itemTitle}
                            </h4>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className="text-zinc-500 text-[8px] font-black uppercase tracking-wider">
                                {sold.itemCategory}{sold.itemSubcategory ? ` > ${sold.itemSubcategory}` : ''}
                              </span>
                              <span className="text-zinc-700">•</span>
                              <span className="text-zinc-400 font-mono text-[9px]">{sold.orderDate}</span>
                            </div>
                          </div>
                        </div>

                        {/* Customer Info */}
                        <div className="flex flex-col min-w-[200px]">
                          <span className="text-[7px] text-zinc-600 font-black uppercase tracking-widest">
                            {language === 'en' ? 'Purchased By' : 'Comprador'}
                          </span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-white font-black text-xs uppercase truncate">{sold.userName}</span>
                            <button
                              onClick={() => handleOpenChatWithUser(sold.userEmail, sold.userName)}
                              className="p-1 text-[#c084fc] hover:text-white transition-colors"
                              title={language === 'en' ? 'Chat with buyer' : 'Abrir chat com comprador'}
                            >
                              <MessageSquare className="w-3 h-3" />
                            </button>
                          </div>
                          <span className="text-zinc-500 font-mono text-[9px] truncate">{sold.userEmail}</span>
                        </div>

                        {/* Price & Order Status */}
                        <div className="flex items-center gap-8">
                          <div>
                            <span className="text-[7px] text-zinc-600 font-black uppercase tracking-widest">
                              {language === 'en' ? 'Amount' : 'Valor Total'}
                            </span>
                            <div className="text-[#c084fc] font-mono text-sm font-black mt-0.5">
                              $ {sold.totalAmount.toFixed(2)}
                              {sold.quantity > 1 && <span className="text-[9px] text-zinc-500 ml-1">({sold.quantity}x)</span>}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1.5 text-[8px] font-black uppercase tracking-wider border rounded ${
                              sold.orderStatus === OrderStatus.COMPLETED
                                ? 'bg-green-500/10 border-green-500/30 text-green-400'
                                : sold.orderStatus === OrderStatus.CANCELLED
                                ? 'bg-red-500/10 border-red-500/30 text-red-400'
                                : 'bg-[#c084fc]/10 border-[#c084fc]/30 text-[#c084fc]'
                            }`}>
                              {sold.orderStatus}
                            </span>
                            
                            <button
                              onClick={() => {
                                setUsersSubTab('users');
                                setSelectedUserEmail(sold.userEmail);
                              }}
                              className="px-3 py-1.5 bg-zinc-900 hover:bg-[#c084fc] text-zinc-400 hover:text-white text-[8px] font-black uppercase transition-all"
                              title={language === 'en' ? 'View User History' : 'Ver Histórico do Usuário'}
                            >
                              <Eye className="w-3 h-3 inline mr-1" />
                              {language === 'en' ? 'History' : 'Histórico'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ===================== SUB-VIEW 3: TOP BEST-SELLERS RANKING ===================== */}
            {usersSubTab === 'ranking' && (
              <div className="space-y-10">
                {/* Top 3 Podium Cards */}
                {topSellingRanking.length >= 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {topSellingRanking.slice(0, 3).map((item, index) => {
                      const medalColors = [
                        { border: 'border-amber-400/50', bg: 'bg-amber-500/10', text: 'text-amber-400', label: '#1 Top Best-Seller' },
                        { border: 'border-zinc-300/40', bg: 'bg-zinc-300/10', text: 'text-zinc-300', label: '#2 Best-Seller' },
                        { border: 'border-amber-700/40', bg: 'bg-amber-700/10', text: 'text-amber-600', label: '#3 Best-Seller' },
                      ][index];

                      return (
                        <div
                          key={item.artId + index}
                          className={`bg-zinc-950/70 border ${medalColors.border} p-6 relative overflow-hidden flex flex-col justify-between group`}
                        >
                          <div>
                            <div className="flex justify-between items-center mb-4">
                              <span className={`px-2.5 py-1 ${medalColors.bg} ${medalColors.text} text-[8px] font-black uppercase tracking-wider rounded border ${medalColors.border}`}>
                                {medalColors.label}
                              </span>
                              <Award className={`w-5 h-5 ${medalColors.text}`} />
                            </div>

                            <div className="aspect-video bg-black overflow-hidden border border-white/5 mb-4 relative">
                              {isVideoUrl(item.videoUrl || item.imageUrl) ? (
                                <video src={item.videoUrl || item.imageUrl} autoPlay loop muted className="w-full h-full object-cover" />
                              ) : (
                                <img src={item.imageUrl || MASTER_FALLBACK_IMAGE} alt={item.title} className="w-full h-full object-cover" />
                              )}
                            </div>

                            <h4 className="text-white font-black text-xs uppercase truncate group-hover:text-[#c084fc] transition-colors">
                              {item.title}
                            </h4>
                            <p className="text-zinc-600 text-[8px] font-black uppercase tracking-widest mt-0.5">{item.category}</p>
                          </div>

                          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/5 mt-4">
                            <div>
                              <span className="text-[7px] text-zinc-500 font-black uppercase block">Unidades Vendidas</span>
                              <span className="text-white font-mono text-sm font-black">{item.unitsSold} un</span>
                            </div>
                            <div>
                              <span className="text-[7px] text-zinc-500 font-black uppercase block">Receita Gerada</span>
                              <span className="text-green-400 font-mono text-sm font-black">$ {item.revenue.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Complete Ranking Table */}
                <div className="bg-zinc-950/50 border border-white/5 overflow-hidden">
                  <div className="p-6 bg-black/40 border-b border-white/5">
                    <h4 className="text-white font-black uppercase text-xs tracking-wider flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#c084fc]" />
                      {language === 'en' ? 'All Products Sales Ranking' : 'Ranking Geral de Desempenho de Vendas'}
                    </h4>
                  </div>

                  {topSellingRanking.length === 0 ? (
                    <div className="p-12 text-center text-zinc-500 text-xs font-black uppercase">
                      {language === 'en' ? 'No sales recorded yet.' : 'Nenhuma venda registrada ainda.'}
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {topSellingRanking.map((item, idx) => (
                        <div
                          key={item.artId + idx}
                          className="p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            <span className="w-6 text-center text-xs font-mono font-black text-zinc-500">
                              #{idx + 1}
                            </span>
                            <div className="w-12 h-12 bg-black border border-white/10 overflow-hidden flex-shrink-0">
                              {isVideoUrl(item.videoUrl || item.imageUrl) ? (
                                <video src={item.videoUrl || item.imageUrl} autoPlay loop muted className="w-full h-full object-cover" />
                              ) : (
                                <img src={item.imageUrl || MASTER_FALLBACK_IMAGE} alt={item.title} className="w-full h-full object-cover" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <h5 className="text-white font-black text-xs uppercase truncate">{item.title}</h5>
                              <p className="text-zinc-500 text-[8px] font-black uppercase tracking-wider">{item.category}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-8 self-end sm:self-auto">
                            <div className="text-right">
                              <span className="text-[7px] text-zinc-600 font-black uppercase block">Unidades</span>
                              <span className="text-white font-mono text-xs font-bold">{item.unitsSold}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-[7px] text-zinc-600 font-black uppercase block">Pedidos</span>
                              <span className="text-zinc-400 font-mono text-xs">{item.ordersCount}</span>
                            </div>
                            <div className="text-right min-w-[80px]">
                              <span className="text-[7px] text-zinc-600 font-black uppercase block">Faturamento</span>
                              <span className="text-green-400 font-mono text-xs font-black">$ {item.revenue.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'chats' && (
          <div className="h-[700px] flex gap-8 animate-in fade-in duration-500">
            <div className="w-96 bg-zinc-950 border border-white/5 flex flex-col">
               <div className="p-8 border-b border-white/5 bg-zinc-900/40"><h3 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-3"><MessageSquare className="w-4 h-4 text-[#c084fc]" /> Atendimento Direto</h3></div>
               <div className="flex-1 overflow-y-auto custom-scrollbar">
                  {chatThreads.map((thread) => {
                    const resolvedName = resolveCustomerDisplayName(thread.userEmail, thread.userName);
                    return (
                      <button key={thread.userEmail} onClick={() => setSelectedThreadEmail(thread.userEmail)} className={`w-full p-8 text-left border-b border-white/5 transition-all hover:bg-white/5 relative ${selectedThreadEmail === thread.userEmail ? 'bg-[#c084fc]/10' : ''}`}>
                        {thread.unreadCount > 0 && <span className="absolute top-8 right-8 w-2 h-2 bg-[#c084fc] rounded-full animate-pulse" />}
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-black text-[10px] uppercase truncate tracking-tight">{resolvedName}</div>
                            <div className="text-zinc-600 text-[8px] font-black uppercase truncate mt-1">{thread.userEmail}</div>
                          </div>
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDeleteChatThread(thread.userEmail); }}
                            className="p-2 text-zinc-700 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </button>
                    );
                  })}
               </div>
            </div>
            <div className="flex-1 bg-zinc-950 border border-white/5 flex flex-col relative overflow-hidden">
               {selectedThreadEmail ? (
                 <>
                   <div className="p-8 border-b border-white/5 bg-zinc-900/40 flex justify-between items-center">
                      <div className="flex flex-col">
                        <h4 className="text-white font-black uppercase text-xs tracking-tighter">
                          {resolveCustomerDisplayName(selectedThreadEmail, chatThreads.find(t => t.userEmail === selectedThreadEmail)?.userName)}
                        </h4>
                        <span className="text-zinc-600 text-[8px] font-black uppercase tracking-widest">{selectedThreadEmail}</span>
                      </div>
                      <button onClick={refreshChats} className="p-3 bg-zinc-900 text-[#c084fc] hover:bg-[#c084fc] hover:text-white transition-all"><RefreshCw className="w-4 h-4" /></button>
                   </div>
                   <div className="flex-1 overflow-y-auto p-10 space-y-8 bg-black/40 custom-scrollbar">
                      {chatThreads.find(t => t.userEmail === selectedThreadEmail)?.messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                           <div className={`max-w-[85%] p-6 text-xs border relative ${msg.sender === 'admin' ? 'bg-[#c084fc]/10 border-[#c084fc]/20 text-white' : 'bg-zinc-900 border-white/10 text-zinc-300'}`}>
                              <div className="absolute -top-3 left-0 bg-black px-2 text-[7px] font-black uppercase text-zinc-700 tracking-widest">{msg.sender}</div>
                              {msg.imageUrl && <img src={msg.imageUrl} className="w-full mb-4 rounded border border-white/5 shadow-2xl" />}
                              <p className="leading-relaxed">{msg.text}</p>
                              <div className="text-[7px] mt-4 opacity-50 uppercase font-black tracking-widest flex items-center gap-2"><Clock className="w-2.5 h-2.5" /> {msg.timestamp}</div>
                           </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                   </div>
                   <div className="p-8 bg-zinc-900/40 border-t border-white/5">
                      <div className="flex gap-4">
                        <input type="text" placeholder="Transmitir..." value={adminReply} onChange={(e) => setAdminReply(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSendAdminReply()} className="flex-1 bg-black border border-zinc-800 p-5 text-white text-xs outline-none focus:border-[#c084fc] transition-all" />
                        <button onClick={handleSendAdminReply} className="bg-white hover:bg-[#c084fc] text-black hover:text-white px-10 py-5 text-[10px] font-black uppercase transition-all shadow-xl active:scale-95">Transmitir</button>
                      </div>
                   </div>
                 </>
               ) : (
                 <div className="flex-1 flex flex-col items-center justify-center opacity-10 text-center px-10">
                   <MessageSquare className="w-32 h-32 mb-10 text-white" />
                   <h4 className="text-xl font-black text-white uppercase tracking-tighter">Câmara de Atendimento</h4>
                 </div>
               )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
